// Guide article content, kept as plain data (not JSX) so the render layer
// (GuideArticlePage) and the build-time prerenderer (scripts/prerender-guides.mjs)
// can share one source of truth. Block types: p, ul, ol, callout.
// Inline [label](url) syntax renders as a link, see src/components/GuideBlocks.jsx.

const updated = { published: "2026-07-01", updated: "2026-08-19", updatedLabel: "19 August 2026" };
const fresh = { published: "2026-08-22", updated: "2026-08-22", updatedLabel: "22 August 2026" };

export const guideContent = {
  serviceboss: {
    "how-to-check-car-service-history": {
      slug: "/guides/how-to-check-car-service-history",
      title: "How to check a car's service history online",
      category: "how-to",
      intro: "You can check much of a car's service history online using its registration, but no single public database holds all of it. This guide sets out which checks work, what order to run them in, and what to do when the record has gaps.",
      metaTitle: "How to Check a Car's Service History Online",
      metaDescription: "How to check a car's service history online: checks by registration, manufacturer records, MOT cross-checks and what to do if nothing turns up.",
      published: "2026-10-31",
      updated: "2026-10-31",
      updatedLabel: "31 October 2026",
      sections: [
        {
          heading: "Can you check a car's service history online?",
          blocks: [
            { type: "p", text: "Yes, in part. A car's service history is the record of the maintenance it has had, and it is split between the manufacturer's dealer network, independent garages, the service book and the owner's paperwork. No public database in the UK brings all of that together, so an online check only ever shows part of the picture." },
            { type: "p", text: "DVLA does not hold servicing information, and the free government MOT service covers MOT tests, not services. The service visits themselves sit with the manufacturer, in a digital record that dealers and registered garages add to. Those systems are generally open only to dealers and registered workshops, although some brands also offer an app to the owner. A buyer has no direct way in, which leaves the routes below." },
          ],
        },
        {
          heading: "What are the ways to check service history online?",
          blocks: [
            { type: "p", text: "There are four useful routes. None of them is complete on its own, and they work best when you use them together." },
            {
              type: "ul",
              items: [
                { strong: "A service history check by registration:", text: "this is the one route that needs nothing from the seller. You enter the registration, and a paid report returns the visits held on the manufacturer's record, with the date, the mileage, the garage and, where it is recorded, the work done. A [ServiceBoss service history check](/service-history-check) covers major manufacturers for cars from model year 2012. If no records are found, a Complete History Report ordered on its own costs you nothing." },
                { strong: "The manufacturer's own app or account:", text: "many makers now let owners see the record themselves. Mercedes-Benz gives owners access to its [Digital Service Booklet](/service-history-check/mercedes) through Mercedes me, and Ford owners can see their [Digital Service Record](/service-history-check/ford) through a Ford account. These belong to the owner, so as a buyer you will need the seller to show you." },
                { strong: "The free MOT history:", text: "the [government's MOT history service](https://www.gov.uk/check-mot-history) shows test results, advisories and recorded mileage for any registration. It is not a service record, but it is a strong cross-check, because the mileage is recorded separately from the servicing." },
                { strong: "A dealer or garage:", text: "this is not strictly online, but an email or phone call is often the next step. Franchised dealers can usually retrieve the manufacturer's record, and independent garages may keep their own. Expect to be asked for proof that you own the car, so a buyer normally has to ask the seller to request it." },
              ],
            },
            { type: "reg-form", brand: "serviceboss", heading: "Check a car's service history by registration" },
          ],
        },
        {
          heading: "How do you check a car's service history, step by step?",
          blocks: [
            { type: "p", text: "The order matters, because the free checks can rule a car out before you spend anything. Work through these once you have found a car you are serious about, and before you travel to see it." },
            {
              type: "ol",
              items: [
                "Get the registration. It is often visible in the advert's photos. If it is not, ask the seller for it, and be wary if they will not give it to you.",
                "Run the free MOT history. Note the mileage at each test and any advisories that keep coming back. A car with an obvious problem can stop here, at no cost.",
                "Run a service history check. Set the visits against the MOT mileages. The two should climb together over the same years.",
                "Ask the seller for the rest. Request the service book and invoices, and ask to see the manufacturer's app if they use one. Some modern cars also show their history on the infotainment screen, so look at that when you view the car.",
                "Ring a garage or two. Pick a stamp or invoice and ask the garage to confirm the visit. Some may be cautious about sharing details because of data protection, but many will confirm that the work took place.",
                "Put a price on what you found. Add up anything overdue or missing before you make an offer, so that you are negotiating from the record and not from a feeling.",
              ],
            },
          ],
        },
        {
          heading: "How do you read what you find?",
          blocks: [
            { type: "p", text: "Reading a service record comes down to one question: does it fit the car and the seller's story? These are the four things to check first." },
            {
              type: "ul",
              items: [
                { strong: "The gaps:", text: "find out the car's service interval before you judge them. Some cars run on a flexible schedule and can properly go up to two years between visits, while others are due every year. A low-mileage car still needs visits for its age, because oil and brake fluid degrade with time." },
                { strong: "The mileage:", text: "it should climb steadily from visit to visit, and match the MOT history. A drop, or a jump that does not fit the car's age, needs an explanation. Our guide to [spotting a mileage discrepancy](/guides/spotting-a-mileage-discrepancy) explains what can cause one." },
                { strong: "The garage:", text: "if an advert says full main dealer history, you should see main dealer visits on the record. For the difference between that phrase and the real thing, read [what full service history really means](/guides/what-full-service-history-really-means)." },
                { strong: "The big jobs:", text: "look for the expensive items that fall due at a set age or mileage, such as a [cambelt](/guides/cambelt-changes-and-service-records) or gearbox oil. If one is due soon and has not been done, you have something real to negotiate with." },
              ],
            },
            { type: "p", text: "If you are not sure what should have been done by a certain age, a [typical car maintenance schedule](https://www.motco.co.uk/guides/maintenance-schedule) gives you a general idea of what falls due and when, although the car's own handbook has the final word." },
          ],
        },
        {
          heading: "What if nothing shows up online?",
          blocks: [
            { type: "p", text: "A blank result is common and has several ordinary explanations. The car may have been looked after by an independent garage that does not add to the digital record. It may have been serviced before that manufacturer's digital records began. Its make may not be covered by the check you ran. On some makes, the owner's digital record has to be activated, and it only logs work from that point on. Peugeot says this about its own." },
            { type: "callout", tone: "note", title: "A blank record is not proof of neglect", text: "A digital record only shows what was logged. No records found does not mean that the car was never serviced, so treat it as a reason to ask for the paperwork and not as a verdict on the car." },
            { type: "p", text: "Next, ask for the service book and every invoice the seller has. Check that the dates and mileages agree with one another and with the MOT history. An invoice tells you more than a stamp, because it lists the work and the parts. Ring the garage named on it if you can." },
            { type: "p", text: "If there is no history anywhere, you are buying on the car's condition and on an inspection alone. That can still be a sensible purchase at the right price, so budget for maintenance that may have been missed, such as a full service and any age-related jobs, and reflect that in your offer." },
          ],
        },
        {
          heading: "Can you trust a digital service record?",
          blocks: [
            { type: "p", text: "You can trust it more than a stamp, but not without question. A digital record is added by a registered garage and the owner cannot edit it, so it is much harder to fake than a stamped page. It also comes from somewhere other than the person selling you the car, which is what makes it useful." },
            { type: "p", text: "Fabricated paper histories are uncommon, but they do happen, so check that invoices and stamps agree on dates and mileage. Digital entries can have errors too, because a garage can type a figure incorrectly, and that usually shows up as a one-off blip and not a pattern." },
            { type: "p", text: "Remember what a record is. It tells you what was logged at the time, not how the car is today. A complete record does not replace a test drive, and it says nothing about finance, theft or write-offs, which are separate checks. A [Complete History Report](/pricing) includes those as well." },
          ],
        },
      ],
      faq: [
        { q: "Can I check a car's service history for free?", a: "Only partly. The MOT history is free and useful as a cross-check, but service visits are not public. Reading the manufacturer's record by registration is a paid check, unless the seller shows you the record or paperwork themselves." },
        { q: "Can I check a car's service history without the V5C or service book?", a: "Yes. A check by registration only needs the registration. If you ask a dealer directly instead, expect to be asked for proof that you own the car, and the V5C alone may not be enough, because it only shows the registered keeper." },
        { q: "How far back does an online service history go?", a: "It depends on when that manufacturer's dealers started recording visits digitally, and a ServiceBoss check covers cars from model year 2012. Earlier work is more likely to be in the service book or on invoices." },
      ],
      note: "A service history check is one part of buying a used car. It does not replace a test drive or, on anything you are serious about, an independent inspection.",
    },
    "manufacturer-service-history-explained": {
      slug: "/guides/manufacturer-service-history-explained",
      title: "Manufacturer service history, explained",
      category: "jargon-buster",
      intro: "What these records actually show, where they fall short, and how to use them properly when you're checking a used car.",
      metaTitle: "Manufacturer Service History Explained",
      metaDescription: "What manufacturer service history shows, what it doesn't, and how to read it before you buy a used car. A plain-language guide from ServiceBoss.",
      ...updated,
      sections: [
        {
          heading: "What manufacturer service history is",
          blocks: [
            { type: "p", text: "Manufacturer service history is a digital record, kept by the car maker, of servicing carried out at its authorised dealer network. It lists the date, the mileage at the time, roughly what work was done, and often which dealership did it. For cars from around model year 2012 onward, it's usually available by registration number alone, without needing the paper service book." },
            { type: "p", text: "It's harder to fake than a stamped book because it comes straight from the dealer network rather than from the seller. If a listing claims \"full main dealer history,\" this record is the fastest way to check that. A [ServiceBoss report](/vehicle-check) pulls it together with mileage and garage details in one search." },
          ],
        },
        {
          heading: "What it won't tell you",
          blocks: [
            { type: "p", text: "A clean service record only confirms the car was serviced on schedule at authorised garages. It says nothing about:" },
            { type: "ul", items: ["Whether the car has outstanding finance owed on it", "Whether it's recorded as an insurance write-off (Cat N or Cat S)", "Whether it's ever been reported stolen", "Mileage recorded outside the dealer network, which can still reveal inconsistencies a service record alone would miss"] },
            { type: "p", text: "Those come from DVLA, insurer and finance-house data, not manufacturer systems. If you want finance, write-off status and mileage checked together in one search, a full vehicle history check such as [MOTCO's](https://www.motco.co.uk/car-history-check) covers the gaps a manufacturer record leaves open." },
          ],
        },
        {
          heading: "How to read the record correctly",
          blocks: [
            { type: "ul", items: [
              { strong: "Gaps between services:", text: "a missed interval isn't automatically a problem, but a run of large gaps is worth asking the seller about." },
              { strong: "Mileage at each visit:", text: "it should climb steadily. A drop, or a jump that doesn't match the car's age, is a flag." },
              { strong: "Coverage window:", text: "records typically only go back to when the manufacturer's digital system started tracking that model, so an older car may show a shorter history even if it was always serviced properly." },
            ] },
            { type: "callout", tone: "tip", title: "A missing record isn't proof of anything", text: "It may have been maintained by an independent garage, or the model may predate the manufacturer's digital coverage. Ask for paper receipts in that case rather than walking away automatically." },
          ],
        },
      ],
      faq: [
        { q: "Does a full service history increase a car's value?", a: "Usually yes, though the effect varies by make and age. A verified digital record is worth more to buyers than an unverifiable stamped book, since it can't be filled in after the fact." },
        { q: "How far back does manufacturer service history go?", a: "It depends on when that manufacturer's dealer network started recording digitally, typically somewhere around 2012 onward for most mainstream brands, sometimes later." },
      ],
      note: "A service history report is one input into a used-car decision, not a replacement for a physical inspection or, for anything you're seriously considering, an independent mechanical inspection.",
    },

    "spotting-a-mileage-discrepancy": {
      slug: "/guides/spotting-a-mileage-discrepancy",
      title: "How to spot a mileage discrepancy",
      category: "how-to",
      intro: "A mileage figure on its own doesn't tell you much. It's the pattern across several readings that matters.",
      metaTitle: "How to Spot a Mileage Discrepancy",
      metaDescription: "Where mileage readings come from, why they sometimes don't match, and what a mismatch usually means. A guide from ServiceBoss.",
      ...updated,
      sections: [
        {
          heading: "Where mileage readings come from",
          blocks: [
            { type: "p", text: "No single body owns a complete mileage record. Readings get logged at MOT tests, at manufacturer service visits, and sometimes by previous owners on sale listings. Each is a separate snapshot taken at a different time by a different party, and they only get compared when someone pulls them together. A mileage check is really a consistency check: it's not confirming a single number, it's confirming the numbers climb the way a normal car's would." },
          ],
        },
        {
          heading: "What a genuine discrepancy looks like",
          blocks: [
            { type: "ul", items: [
              { strong: "A drop between readings.", text: "Mileage should only go up. Any recorded drop is worth asking about directly." },
              { strong: "An unusually small jump for an older car.", text: "Very low annual mileage isn't impossible, but combined with heavy wear elsewhere, it's worth questioning." },
              { strong: "A large, sudden jump.", text: "Possible with genuine high-mileage use, but also consistent with a clocking attempt if it doesn't match the seller's story." },
            ] },
            { type: "p", text: "A single odd-looking gap doesn't confirm tampering on its own. Odometers can be replaced during a repair, a car can sit unused for a long stretch, or a reading can simply be entered wrong at a garage. Treat an inconsistency as a reason to ask questions, not proof by itself." },
          ],
        },
        {
          heading: "Running a check before you commit",
          blocks: [
            { type: "p", text: "The most useful version of this check pulls readings from multiple points across the car's life, not just the most recent MOT. A dedicated mileage check, like the one [MOTCO runs against DVSA's MOT history](https://www.motco.co.uk/mileage-check), lines those readings up so a gap is easy to spot rather than something you have to work out from paperwork yourself. A [ServiceBoss report](/vehicle-check) does the same alongside manufacturer service dates." },
            { type: "callout", tone: "note", title: "Corroborate, don't just trust one source", text: "Ask to see the car's V5C and any old MOT certificates alongside whatever check you run. Paper records and a digital check agreeing is a stronger signal than either alone." },
          ],
        },
      ],
      faq: [
        { q: "What counts as a normal annual mileage in the UK?", a: "Roughly 7,000 to 8,000 miles a year for an average private car, though this varies a lot by usage. It's a rough benchmark, not a rule." },
        { q: "Can a mileage check be wrong?", a: "Yes, if a garage entered a figure incorrectly at the time of a service or MOT. That's a data-entry error, not tampering, and it's usually a one-off gap rather than a pattern." },
      ],
    },

    "car-valuations-explained": {
      slug: "/guides/car-valuations-explained",
      title: "What a car valuation is actually based on",
      category: "jargon-buster",
      intro: "A car valuation is an estimate, not a fixed price. Here's what actually drives the number.",
      metaTitle: "What a Car Valuation Is Based On",
      metaDescription: "How online car valuations are calculated, what moves the number, and how much to trust it before buying or selling. A guide from ServiceBoss.",
      ...updated,
      sections: [
        {
          heading: "What a valuation actually is",
          blocks: [
            { type: "p", text: "A car valuation is an estimate of what a specific vehicle is likely to sell for, based on recent sale prices of similar cars: same make, model, age, mileage and condition band. It isn't a guaranteed price. Two valuation tools can give different figures for the same car because they draw on different pools of sale data." },
          ],
        },
        {
          heading: "What moves the number",
          blocks: [
            { type: "ul", items: [
              { strong: "Mileage and age:", text: "the two biggest factors. Higher mileage and older cars are worth less, roughly along a depreciation curve specific to that model." },
              { strong: "Condition and history:", text: "a full service history, no accident record, and tidy condition all push the valuation up." },
              { strong: "Colour, trim and options:", text: "less common colours and desirable trim levels can move the price a few percent either way." },
              { strong: "Market demand:", text: "fuel type and body style matter too. Demand for some fuel types has shifted noticeably in recent years, which changes resale value independent of the car itself." },
            ] },
          ],
        },
        {
          heading: "Using a valuation properly",
          blocks: [
            { type: "p", text: "Treat a valuation as a starting point for negotiation, not a fixed number to insist on. If you want a valuation alongside a full history check, checking finance, write-off and mileage status too, [MOTCO's valuation tool](https://www.motco.co.uk/valuation) covers that in one place." },
            { type: "callout", tone: "tip", title: "Get more than one figure", text: "Run two or three valuations and look at the range rather than a single number. A wide spread usually means the car has less common options or an unusual history that's harder to price automatically." },
          ],
        },
      ],
      faq: [
        { q: "Is an online car valuation accurate?", a: "It's a reasonable estimate for typical cars, but it can be less reliable for rare trims, recent price swings, or vehicles with unusual histories that automated pricing doesn't fully capture." },
        { q: "Does a car valuation account for damage?", a: "Only if you tell it about the damage. Most tools ask for a general condition rating; they don't inspect the car, so undisclosed damage isn't reflected in the number." },
      ],
    },

    "hpi-clear-explained": {
      slug: "/guides/hpi-clear-explained",
      title: "HPI clear: what it means and what it doesn't cover",
      category: "jargon-buster",
      intro: "\"HPI clear\" gets used loosely in car listings. Here's what the term actually promises.",
      metaTitle: "HPI Clear Explained",
      metaDescription: "What an HPI clear result actually checks, what it doesn't cover, and how to read one properly before buying a used car. A guide from ServiceBoss.",
      ...updated,
      sections: [
        {
          heading: "What HPI clear means",
          blocks: [
            { type: "p", text: "HPI is a UK vehicle-check brand, and \"HPI clear\" has become shorthand for a car passing an outstanding-finance, write-off, stolen and scrapped check with no markers found. Sellers use the phrase generically now, similar to how some people say \"hoover\" for any vacuum cleaner, even when the check was actually run through a different provider entirely." },
          ],
        },
        {
          heading: "What it checks, and what it skips",
          blocks: [
            { type: "p", text: "A clear result on this type of check typically confirms:" },
            { type: "ul", items: ["No outstanding finance recorded against the vehicle", "Not recorded as an insurance write-off", "Not reported stolen", "Not recorded as scrapped by DVLA"] },
            { type: "p", text: "It does not confirm the car's mechanical condition, verify the mileage is genuine, or guarantee the seller is the legal owner. A clear result is a good sign, but it answers a narrower question than \"is this car okay to buy\": it's specifically about financial and legal history markers." },
          ],
        },
        {
          heading: "Running the check yourself",
          blocks: [
            { type: "p", text: "Don't take a seller's word that a car is \"HPI clear.\" Run your own check against the registration before agreeing a price. [MOTCO's HPI-style check](https://www.motco.co.uk/hpi-check) covers finance, write-off and stolen markers in one search, and a fresh result is worth more than a screenshot the seller shows you, since anyone's check is only accurate at the moment it's run." },
            { type: "callout", tone: "note", title: "A screenshot proves nothing", text: "Results can be edited, or simply be from a different car. Always run the check yourself against the exact registration in front of you." },
          ],
        },
      ],
      faq: [
        { q: "Is HPI the only company that does this kind of check?", a: "No. HPI is one well-known brand, but several UK providers run equivalent finance, write-off and stolen checks against the same underlying data sources." },
        { q: "Does an HPI clear result mean the car has no problems?", a: "No. It only covers finance, write-off, theft and scrap-status markers, not mechanical condition, genuine mileage, or ownership." },
      ],
    },

    "buying-a-car-privately-checklist": {
      slug: "/guides/buying-a-car-privately-checklist",
      title: "Buying a car privately: a step-by-step checklist",
      category: "how-to",
      intro: "Buying from a private seller cuts out dealer fees, but it also cuts out dealer protections. Here's what to check first.",
      metaTitle: "Buying a Car Privately: Step-by-Step Checklist",
      metaDescription: "A step-by-step checklist for buying a used car from a private seller: documents, checks and payment, done safely. A guide from ServiceBoss.",
      ...updated,
      sections: [
        {
          heading: "Before you view the car",
          blocks: [
            { type: "ol", items: [
              "Ask for the registration number and run a history check before travelling to see it, so a serious problem doesn't waste your trip.",
              "Check the seller's name matches the V5C keeper details they'll show you in person.",
              "Confirm the car's current MOT status and expiry date online before you go.",
            ] },
          ],
        },
        {
          heading: "When you view it",
          blocks: [
            { type: "ul", items: [
              { strong: "Meet at the seller's registered address,", text: "not a car park or motorway services. A seller unwilling to do this is a warning sign." },
              { strong: "Check the VIN in three places", text: "(dashboard, door pillar, V5C) all match." },
              { strong: "Compare the mileage on the odometer", text: "against your history check and any paperwork." },
              { strong: "Ask direct questions about accident history, ownership length, and reason for selling.", text: "" },
            ] },
          ],
        },
        {
          heading: "Paying and completing the sale",
          blocks: [
            { type: "p", text: "Never pay in full before seeing the car in person. Get a receipt with both parties' names, the date, the price, the registration, and \"sold as seen\" (or otherwise) clearly stated. Complete the V5C keeper-change section together, and tell DVLA of the change as soon as possible, rather than leaving it to the seller." },
            { type: "callout", tone: "tip", title: "Run the check again, right before paying", text: "If time has passed since your first search, a lot can change. A fresh [vehicle history check](https://www.motco.co.uk/car-check) from MOTCO right before you hand over money confirms nothing has shifted since you first looked." },
          ],
        },
      ],
      faq: [
        { q: "Do I need to see the seller's ID when buying privately?", a: "It's not a legal requirement, but it's good practice. Comparing their ID to the V5C keeper name is a simple way to reduce the risk of buying from someone who isn't entitled to sell the car." },
        { q: "What happens if I buy a car with hidden finance owed on it?", a: "The finance company can, in some circumstances, repossess the car even from a buyer who acted in good faith. Running a finance check before paying is the way to avoid this." },
      ],
    },

    "dvsa-mot-data-explained": {
      slug: "/guides/dvsa-mot-data-explained",
      title: "DVSA MOT data: what's public and what isn't",
      category: "know-the-rules",
      intro: "DVSA's MOT history is one of the few genuinely free, official vehicle records in the UK. Here's exactly what it shows.",
      metaTitle: "DVSA MOT Data Explained",
      metaDescription: "What DVSA's free MOT history check shows, what it doesn't cover, and how to use it properly before buying a used car. A guide from ServiceBoss.",
      ...updated,
      sections: [
        {
          heading: "What DVSA publishes",
          blocks: [
            { type: "p", text: "DVSA (the Driver and Vehicle Standards Agency) publishes MOT test history for free by registration number, for any vehicle that's had at least one MOT. Each test entry shows the date, result (pass, fail or advisory notes), the mileage recorded at the time, and the expiry date if it passed. This is one of the more reliable public records in the UK, since garages submit results directly, and it can't be edited by a seller afterward." },
          ],
        },
        {
          heading: "What it doesn't show",
          blocks: [
            { type: "ul", items: ["Finance owed on the vehicle", "Insurance write-off category", "Stolen markers", "Full servicing history (only MOT-related work and advisories, not routine servicing)"] },
            { type: "p", text: "It's also limited to cars over three years old, since that's when the first MOT is due, so a newer car will show little or no history yet regardless of how it's been looked after." },
          ],
        },
        {
          heading: "Reading advisories correctly",
          blocks: [
            { type: "p", text: "An advisory isn't a fail; it's a note that something is close to needing attention but wasn't bad enough to fail the test on the day. A pattern of the same advisory recurring across several years, without ever being fixed, is worth asking the seller about directly. For a fuller picture that includes finance and write-off status alongside the MOT record, [MOTCO's MOT history checker](https://www.motco.co.uk/dvsa-details) pulls DVSA's data together with those other checks." },
            { type: "callout", tone: "tip", title: "Mileage in MOT history is a good sanity check", text: "Since garages enter it independently at each test, comparing it against the seller's claimed mileage is one of the simplest checks you can run for free." },
          ],
        },
      ],
      faq: [
        { q: "Is DVSA MOT history the same as a full vehicle history check?", a: "No. It's free and official, but it only covers MOT test results and mileage at each test. It doesn't include finance, write-off or stolen-vehicle data." },
        { q: "How far back does MOT history go?", a: "Typically back to the car's first MOT, roughly three years after first registration, so a car's full MOT history is usually available for as long as it's been on the road past that point." },
      ],
    },

    "ev-charging-checks-before-buying": {
      slug: "/guides/ev-charging-checks-before-buying",
      title: "EV charging: what to check before buying an electric car",
      category: "how-to",
      intro: "Range on paper and range in practice aren't the same thing. Here's what actually matters before you commit to an electric car.",
      metaTitle: "EV Charging Checks Before Buying",
      metaDescription: "What to check about charging speed, battery health and public charging coverage before buying a used electric car. A guide from ServiceBoss.",
      published: "2026-08-19",
      updated: "2026-08-19",
      updatedLabel: "19 August 2026",
      sections: [
        {
          heading: "Home charging changes the calculation completely",
          blocks: [
            { type: "p", text: "If you can charge at home overnight, day-to-day range matters far less than it looks on paper, since the car starts most days close to full. Without home charging, you're relying on public infrastructure, and coverage varies a lot by area. Check what's realistically near your home and workplace before assuming a given range figure will suit your routine." },
          ],
        },
        {
          heading: "Charging speed: AC vs DC, and what the numbers mean",
          blocks: [
            { type: "ul", items: [
              { strong: "Home/AC charging (up to 7-22kW):", text: "typically an overnight charge, from empty to full in several hours depending on the wallbox and the car's onboard charger limit." },
              { strong: "Public rapid/DC charging (50kW+):", text: "can take a car from low to around 80% in 30-60 minutes, though speed tapers sharply after 80% on most batteries, so the last 20% takes disproportionately longer." },
              { strong: "Ultra-rapid (150kW+):", text: "only useful if the specific car can actually accept that rate. A car limited to 50kW won't charge any faster on a 150kW charger, the car's own limit is what counts, not the charger's maximum." },
            ] },
            { type: "p", text: "Check the car's actual maximum charging rate, not just the charger network's headline speed, the two are frequently different." },
          ],
        },
        {
          heading: "Battery health matters more than mileage",
          blocks: [
            { type: "p", text: "EV batteries degrade gradually with age and charge cycles, so a used EV's real-world range can be meaningfully lower than its original figure, especially on early models. A dealer or seller can often provide a battery health/state-of-health reading. If they can't or won't, treat that as a reason to ask more questions, not assume the worst, but don't skip asking." },
            { type: "callout", tone: "tip", title: "Check public charging coverage before you buy, not after", text: "A quick look at a public charging map for your usual routes tells you far more about day-to-day usability than the manufacturer's quoted range ever will. MOTCO's [EV charging map](https://www.motco.co.uk/ev-charging-map) is a useful way to check coverage before you commit." },
          ],
        },
      ],
      faq: [
        { q: "Does cold weather really reduce EV range?", a: "Yes, noticeably. Range can drop by 10-30% in cold conditions due to battery chemistry and cabin heating, worth factoring in if you're relying on a tight range margin." },
        { q: "Should I buy an EV without home charging?", a: "It's workable if public charging near you is reliable and reasonably fast, but check actual coverage for your routine first rather than assuming it'll be fine." },
      ],
    },

    "what-full-service-history-really-means": {
      slug: "/guides/what-full-service-history-really-means",
      title: "What \"full service history\" really means",
      category: "jargon-buster",
      intro: "Sellers use the phrase constantly. Here's what it should mean in practice, and what it quietly leaves out.",
      metaTitle: "What Full Service History Really Means",
      metaDescription: "What \"full service history\" should mean on a used car advert, how to verify it, and what the phrase never covers. A plain guide from ServiceBoss.",
      ...fresh,
      sections: [
        {
          heading: "What buyers think it means",
          blocks: [
            { type: "p", text: "Most buyers hear \"full service history\" and assume every scheduled service has been done, on time, with paperwork to prove it. That is a reasonable expectation. It is not what every seller is actually offering when they type those three words into an advert." },
            { type: "p", text: "In practice the phrase is unregulated. Nobody audits it before a listing goes live. A seller can mean a complete manufacturer digital record, a folder of independent garage invoices, a stamped book with a few gaps filled in later, or simply \"I think it was looked after.\"" },
          ],
        },
        {
          heading: "What a proper history should include",
          blocks: [
            { type: "p", text: "A useful service history is a dated trail you can check against the car's age and mileage. At minimum you want:" },
            { type: "ul", items: [
              { strong: "Dates and mileage:", text: "each visit should sit in order, with mileage climbing in a way that matches the car's age." },
              { strong: "Who did the work:", text: "main dealer, specialist, or a named independent. The stamp alone is weaker than an invoice with a garage name and VAT number." },
              { strong: "What was done:", text: "oil and filters only is different from a major service with plugs, brake fluid or a cambelt. \"Serviced\" without detail is almost useless." },
            ] },
            { type: "p", text: "Manufacturer digital records are usually the cleanest version of this, because they come from the dealer network rather than from the seller's filing cabinet. A [ServiceBoss report](/vehicle-check) is built around that record, so you can see what is actually logged against the registration rather than what the advert claims." },
          ],
        },
        {
          heading: "What the phrase never covers",
          blocks: [
            { type: "p", text: "Even a genuine, complete service history says nothing about outstanding finance, write-off status, or whether the car was ever reported stolen. Those sit in different databases. A tidy service book can sit on top of a car that still has money owed on it." },
            { type: "p", text: "It also says nothing about quality of the work. A stamp confirms a visit happened. It does not confirm the garage used the right parts, followed the schedule correctly, or caught every developing fault." },
            { type: "callout", tone: "note", title: "Ask for the trail, not the slogan", text: "If a seller says \"full service history,\" ask them to show the dates, mileages and invoices before you travel. If they can only repeat the phrase, treat that as an answer in itself." },
          ],
        },
        {
          heading: "How to verify it before you buy",
          blocks: [
            { type: "ol", items: [
              "Ask whether the history is manufacturer digital, dealer stamped, independent invoices, or a mix.",
              "Check that service dates line up with the car's age and the mileage on the dash.",
              "Look for big gaps, especially around cambelt or major service due dates for that model.",
              "Run a separate risk check for finance and write-off status. Service paperwork does not replace that.",
            ] },
            { type: "p", text: "If the car was imported, the UK digital trail may start late even when the car was serviced abroad. In that case paper records matter more, and an [imported car check from MOTCO](https://www.motco.co.uk/imported-car-check) is worth running so you know when the vehicle entered the UK register." },
          ],
        },
      ],
      faq: [
        { q: "Is independent garage history as good as main dealer history?", a: "It can be, if the invoices are real, dated, and match the mileage. Main dealer history is easier to verify digitally. Independent history needs closer reading of the paperwork." },
        { q: "Should I walk away if there is no service history?", a: "Not automatically, but price it as a risk. No history means you are buying on condition and inspection alone, and you should budget for unknown past maintenance." },
      ],
      note: "Service history helps you judge how a car was maintained. It is not proof of legal title, finance status, or write-off history.",
    },

    "cambelt-changes-and-service-records": {
      slug: "/guides/cambelt-changes-and-service-records",
      title: "Cambelt changes and what service records should show",
      category: "how-to",
      intro: "A missed cambelt interval is one of the expensive surprises in used-car buying. Here's what to look for in the paperwork.",
      metaTitle: "Cambelt Changes and Service Records",
      metaDescription: "How to check whether a used car's cambelt or timing chain work is recorded, what intervals usually mean, and what to ask the seller. From ServiceBoss.",
      ...fresh,
      sections: [
        {
          heading: "Why this one job matters so much",
          blocks: [
            { type: "p", text: "On many engines, the cambelt (timing belt) keeps the crankshaft and camshaft in sync. If it snaps on an interference engine, valves and pistons can collide. That is not a roadside inconvenience. It is often a four-figure repair, sometimes a write-off for an older car." },
            { type: "p", text: "That is why buyers treat cambelt evidence as a separate question from \"has it been serviced.\" A car can have regular oil changes and still be overdue for the belt." },
          ],
        },
        {
          heading: "Belt vs chain, and why the wording matters",
          blocks: [
            { type: "ul", items: [
              { strong: "Timing belt / cambelt:", text: "a rubber belt with a stated replacement interval in years and miles. Both limits matter. A low-mileage car can still be overdue on time alone." },
              { strong: "Timing chain:", text: "usually designed to last longer, but not forever. Rattle on cold start, stretched-chain fault codes, or repeated guidance in MOT advisories can still signal trouble." },
            ] },
            { type: "p", text: "Never assume from the badge alone. Two engines in the same model range can differ. Check the exact engine code or ask a franchise specialist for that car's interval before you rely on a seller's guess." },
          ],
        },
        {
          heading: "What a useful record looks like",
          blocks: [
            { type: "p", text: "A solid cambelt record is more than a handwritten note saying \"belt done.\" You want a date, the mileage at the time, and ideally an invoice listing the belt, tensioner and water pump if they were done together, which is common on many engines." },
            { type: "p", text: "Manufacturer digital history helps when the work was done at a franchise dealer, because the visit is harder to invent after the fact. A [ServiceBoss report](/vehicle-check) can show whether dealer-logged major work sits on the timeline. Independent invoices still count, but read them carefully and match them to the odometer." },
            { type: "callout", tone: "tip", title: "Time and miles both count", text: "If the schedule says \"every 5 years or 60,000 miles, whichever comes first,\" a five-year-old car with 30,000 miles can still be due. Sellers often quote only the mileage figure." },
          ],
        },
        {
          heading: "If the record is missing",
          blocks: [
            { type: "p", text: "No proof does not automatically mean the belt was never done. It does mean you should price the job into the buy, or get it done before you rely on the car for long trips. Asking the seller to reduce the price by a realistic garage quote is a normal part of private buying." },
            { type: "p", text: "Also check whether the model has open safety recalls that could overlap with engine or timing-system work. [MOTCO's recall check](https://www.motco.co.uk/recall-check) is a quick way to see whether the manufacturer has an outstanding notice against that registration before you argue about old invoices." },
          ],
        },
      ],
      faq: [
        { q: "Can I tell from an MOT if the cambelt was changed?", a: "Not reliably. MOTs do not record cambelt replacement as a standard pass item. You need service invoices or a dealer digital record." },
        { q: "Should the water pump be changed with the belt?", a: "On many engines, yes, because the labour overlaps. An invoice that only lists a belt, with no tensioner or pump, is worth questioning with a specialist for that model." },
      ],
      note: "Always confirm the interval for the exact engine fitted. Model-year guides are a starting point, not a substitute for the manufacturer's schedule.",
    },

    "car-polish-vs-wax-vs-sealant": {
      slug: "/guides/car-polish-vs-wax-vs-sealant",
      title: "Car polish vs wax vs sealant: what each one actually does",
      category: "how-to",
      intro: "These three products get mixed up constantly. They do different jobs, and using the wrong one wastes time.",
      metaTitle: "Car Polish vs Wax vs Sealant Explained",
      metaDescription: "What polish, wax and sealant each do on a car's paint, when to use them, and in what order. A plain guide from ServiceBoss.",
      ...fresh,
      sections: [
        {
          heading: "The short version",
          blocks: [
            {
              type: "ul",
              items: [
                {
                  strong: "Polish:",
                  text: "cuts or smooths the clearcoat to reduce swirl marks and light scratches. It changes the surface. It is not protection on its own."
                },
                {
                  strong: "Wax:",
                  text: "adds a sacrificial layer for gloss and short-term protection. Natural and synthetic waxes both sit on top of the paint."
                },
                {
                  strong: "Sealant:",
                  text: "a longer-lasting synthetic layer. Usually less warm shine than wax, but it typically lasts longer if applied properly."
                }
              ]
            },
            {
              type: "p",
              text: "If the paint looks dull or swirled, polish first. Then protect with wax or sealant. Putting wax on top of neglected paint just seals in the haze."
            }
          ]
        },
        {
          heading: "When polish is worth doing",
          blocks: [
            {
              type: "p",
              text: "Polish helps when the paint feels rough after washing, or when swirl marks show up in sunlight. A finishing polish is gentler. A cutting compound removes more defects and also removes more clearcoat, so go carefully on thin or previously refinished panels."
            },
            {
              type: "p",
              text: "If you are buying a used car and the paint looks freshly wet, ask whether it was polished for the advert. Heavy compounding can hide wear for photos without fixing underlying issues. A [ServiceBoss report](/vehicle-check) is still worth running for the maintenance side before you get distracted by a shiny bonnet."
            }
          ]
        },
        {
          heading: "Wax or sealant for protection",
          blocks: [
            {
              type: "p",
              text: "Wax is fine if you like topping it up every few weeks and want an easy wipe-on shine. Sealant suits people who want longer gaps between applications. Ceramic coatings are a separate step up again: more durable when applied well, less forgiving if the prep was rushed."
            },
            {
              type: "callout",
              tone: "tip",
              title: "Wash before you protect",
              text: "Any wax or sealant on dirty or contaminated paint fails early. Decontaminate and dry the car properly first, or you are locking dirt under the layer."
            }
          ]
        },
        {
          heading: "A simple order that works",
          blocks: [
            {
              type: "ol",
              items: [
                "Wash and dry thoroughly.",
                "Remove bonded dirt if the paint still feels rough.",
                "Polish only if defects bother you.",
                "Apply wax or sealant as the final protective step."
              ]
            },
            {
              type: "p",
              text: "If you want a fuller walkthrough of product choice and prep mistakes, MOTCO's guide to [car polish vs wax vs sealant](https://www.motco.co.uk/guides/car-polish-vs-wax-vs-sealant) covers the same split in more detail."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Can I use polish and wax on the same day?",
          a: "Yes. Polish first, remove residue, then apply wax or sealant. Do not mix them into one product unless the label says that is the design."
        },
        {
          q: "Does wax protect against stone chips?",
          a: "No. Wax and sealant help with water beading and light contamination. They do not stop chips or deep scratches."
        }
      ],
      note: "If paint is peeling, cracked or heavily oxidised, stop and get a body-shop opinion before polishing further.",
    },

    "how-to-reduce-road-noise-in-car": {
      slug: "/guides/how-to-reduce-road-noise-in-car",
      title: "How to reduce road noise in a car",
      category: "how-to",
      intro: "Some road roar is normal. A sudden increase usually has a cause you can narrow down before spending money on soundproofing.",
      metaTitle: "How to Reduce Road Noise in a Car",
      metaDescription: "Practical ways to cut road noise in a UK car, from tyres and alignment to simple cabin checks. A guide from ServiceBoss.",
      ...fresh,
      sections: [
        {
          heading: "Find the source before you buy foam",
          blocks: [
            {
              type: "p",
              text: "Road noise usually comes from tyres on coarse surfaces, worn suspension bushes, a door or boot seal that no longer closes tightly, or missing cabin insulation after a repair. Sound deadening mats only help after the cheap faults are ruled out."
            },
            {
              type: "ul",
              items: [
                {
                  strong: "Tyres:",
                  text: "cheap hard-compound tyres and worn tread often roar more. Check pressures first. Soft or uneven pressures change the contact patch and the noise."
                },
                {
                  strong: "Wheel bearings / suspension:",
                  text: "a drone that rises with speed, or a roar that changes when you weave gently, can be mechanical rather than just the road."
                },
                {
                  strong: "Seals and trim:",
                  text: "a whistling gap at motorway speed is often a door seal, mirror base or roof rail, not the tyre."
                }
              ]
            }
          ]
        },
        {
          heading: "Fixes that usually help most",
          blocks: [
            {
              type: "p",
              text: "Start with tyre pressures to the door-card figure, then inspect tread wear patterns. Cupped or feathered edges point to alignment or damper issues. Replacing one noisy tyre with a mismatched brand can make the cabin louder, not quieter, so keep axles matched where you can."
            },
            {
              type: "p",
              text: "If the car has a patchy service history around suspension work, that matters. Worn drop links and bushes transmit more road texture into the body. A [ServiceBoss report](/vehicle-check) will not measure cabin noise, but it can show whether major maintenance was logged before you pay for acoustic upgrades."
            },
            {
              type: "callout",
              tone: "note",
              title: "Quiet tyres are a trade-off",
              text: "Touring tyres often run quieter than aggressive eco or performance patterns. They can also wear differently and change wet grip. Buy for your roads, not just the decibel claim on a review."
            }
          ]
        },
        {
          heading: "When extra soundproofing makes sense",
          blocks: [
            {
              type: "p",
              text: "After tyres, seals and suspension are sorted, adding quality mat under carpets or in doors can take the edge off coarse UK A-roads. It adds weight and cost, and poor fitting can trap moisture. Do the free checks first."
            },
            {
              type: "p",
              text: "For a step-by-step set of cabin and tyre checks, see MOTCO's guide on [how to reduce road noise in a car](https://www.motco.co.uk/guides/how-to-reduce-road-noise-in-car)."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Why is my car louder on some roads only?",
          a: "Coarse chipseal surfaces are noisier than smooth tarmac. If the noise disappears on smooth roads, tyres and surface interaction are the likely cause."
        },
        {
          q: "Can a full exhaust make more cabin noise?",
          a: "Yes. A leaking exhaust or missing heat shield can sound like road roar until you notice it changes with engine load rather than road speed."
        }
      ],
      note: "A sudden new drone with vibration should be checked as a mechanical fault, not treated as a soundproofing project.",
    },

    "how-to-remove-car-dents-at-home": {
      slug: "/guides/how-to-remove-car-dents-at-home",
      title: "How to remove car dents at home, and when not to",
      category: "how-to",
      intro: "Some small dents can be improved at home. Others get worse the moment you try. Knowing the difference saves paint.",
      metaTitle: "How to Remove Car Dents at Home",
      metaDescription: "Which car dents you can attempt at home, which methods are realistic, and when to stop and use a professional. From ServiceBoss.",
      ...fresh,
      sections: [
        {
          heading: "Dents that are realistic DIY jobs",
          blocks: [
            {
              type: "p",
              text: "Shallow dents on flat areas, with paint still intact and no sharp crease, are the ones home methods sometimes improve. Think gentle parking-lot dings on a door skin, not a folded wing edge."
            },
            {
              type: "p",
              text: "If the paint is cracked, the metal is creased, or the dent sits on a body line, stop. Pushing from behind or pulling from the front often turns a cheap paintless repair into a respray."
            }
          ]
        },
        {
          heading: "Methods people try, ranked by risk",
          blocks: [
            {
              type: "ul",
              items: [
                {
                  strong: "Paintless push from behind:",
                  text: "access allowing, controlled pressure behind the panel. Best left to someone who has done it before."
                },
                {
                  strong: "Suction cups / glue tabs:",
                  text: "can lift gentle dents on flat panels. Heat and glue residue need care."
                },
                {
                  strong: "Hot water or hairdryer then plunge:",
                  text: "sometimes moves very shallow plastic-bumper dents. Unreliable on metal, easy to overheat paint."
                }
              ]
            },
            {
              type: "callout",
              tone: "note",
              title: "Dry ice and DIY kits",
              text: "Dramatic internet methods fail often on real UK cars with thicker paint systems. If the first attempt does nothing, repeating harder usually marks the clearcoat."
            }
          ]
        },
        {
          heading: "When to pay a professional instead",
          blocks: [
            {
              type: "p",
              text: "Paintless dent removal on a crease, aluminium panel, or dent near an edge is usually cheaper than a home attempt that breaks paint. Get a quote before you invent tools out of kitchen gear."
            },
            {
              type: "p",
              text: "If you are assessing dents on a car you might buy, photograph them and price the repair into your offer rather than assuming you will pop them out later. Pair that with a proper history check. A tidy body can still sit on a car with finance or write-off history, which a [ServiceBoss report](/vehicle-check) helps you catch before cosmetics distract you."
            },
            {
              type: "p",
              text: "For a clearer split between safe home attempts and leave-it jobs, read MOTCO's guide to [removing car dents at home](https://www.motco.co.uk/guides/how-to-remove-car-dents-at-home)."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Will boiling water fix a metal door dent?",
          a: "Usually not. That trick is mostly for flexible plastic bumpers, and even then results vary."
        },
        {
          q: "Does insurance cover small dents?",
          a: "Often yes, but the excess and future premium can cost more than a local paintless repair. Get both numbers before claiming."
        }
      ],
      note: "If paint is already broken, treat it as a body repair. Moisture under broken paint becomes rust, especially on older steel panels.",
    },
  },

  servicebyreg: {
    "vehicle-risk-checks-before-you-buy": {
      slug: "/guides/vehicle-risk-checks-before-you-buy",
      title: "Vehicle risk checks, before you buy",
      category: "how-to",
      intro: "Finance, write-off status and stolen markers each answer a different question. Here's what each one actually covers.",
      metaTitle: "Vehicle Risk Checks Before You Buy",
      metaDescription: "Outstanding finance, write-off markers and stolen records explained: what each check covers and why they matter before you buy a used car. A guide from ServiceByReg.",
      ...updated,
      sections: [
        {
          heading: "Why \"a history check\" is really several checks",
          blocks: [
            { type: "p", text: "People often ask for \"a history check\" as if it's one lookup, but the useful checks on a used car are really separate questions pulled from different sources:" },
            { type: "ul", items: [
              { strong: "Outstanding finance:", text: "is money still owed on the car under a hire-purchase or PCP agreement, meaning the car isn't fully the seller's to sell?" },
              { strong: "Write-off category:", text: "has an insurer declared the car a total loss, and if so, was it Cat N (non-structural) or Cat S (structural repair)?" },
              { strong: "Stolen markers:", text: "has the vehicle ever been reported stolen and not recovered or resolved?" },
              { strong: "Mileage consistency:", text: "do recorded readings climb steadily across MOT tests and service visits, or is there a jump or drop worth questioning?" },
            ] },
          ],
        },
        {
          heading: "Why outstanding finance matters most",
          blocks: [
            { type: "p", text: "Buying a car with finance still owed on it doesn't automatically make the sale illegal, but it can leave the buyer exposed: if the previous owner defaults, the finance company can in some circumstances repossess the car from whoever's driving it, even if that person bought it in good faith. A finance check flags whether an agreement is still recorded against the registration before that becomes your problem. A [ServiceByReg report](/vehicle-check) includes this alongside the other risk markers, so it's one check rather than several." },
          ],
        },
        {
          heading: "Putting it together",
          blocks: [
            { type: "p", text: "Each of these checks draws on a different data source (DVLA, insurers, finance houses), so no single free lookup covers all of them reliably. If you want finance status, write-off history and mileage checked together in one search, providers like [MOTCO's outstanding finance check](https://www.motco.co.uk/outstanding-finance-check) combine those markers into a single report." },
            { type: "callout", tone: "note", title: "None of this replaces seeing the car in person", text: "Treat a clean report as one good sign among several, not the only thing that matters." },
          ],
        },
      ],
      faq: [
        { q: "How much does a vehicle risk check cost?", a: "Free versions exist but usually only cover one marker or a limited data source. Paid reports combining finance, write-off and mileage in one search typically cost a small fixed fee, well below the cost of buying a car with a hidden problem." },
        { q: "Can I run a vehicle risk check without the owner's permission?", a: "Yes. These checks use only the registration number and public/licensed data sources, so you don't need the current owner's consent to run one." },
      ],
    },

    "cat-n-vs-cat-s-write-offs": {
      slug: "/guides/cat-n-vs-cat-s-write-offs",
      title: "Cat N vs Cat S write-offs explained",
      category: "jargon-buster",
      intro: "Two different write-off categories, decided for two different reasons. Here's what each one actually means for the car.",
      metaTitle: "Cat N vs Cat S Write-Offs Explained",
      metaDescription: "What Cat N and Cat S mean, how insurers assign them, and how each affects a car's value and insurance cost. A guide from ServiceByReg.",
      ...updated,
      sections: [
        {
          heading: "Where the categories come from",
          blocks: [
            { type: "p", text: "When an insurer decides a repair costs more than a car is worth, or the damage is severe enough that repair isn't sensible, they record it as a total loss and assign a damage category under the ABI's code of practice. The category is about what kind of damage the car had, not simply how expensive the repair was." },
          ],
        },
        {
          heading: "Cat S vs Cat N",
          blocks: [
            { type: "ul", items: [
              { strong: "Cat S (structural):", text: "the damage affected the chassis or load-bearing parts. A Cat S car can be legally repaired and put back on the road, and plenty are repaired properly, but structural repairs are harder to verify from the outside, so quality depends entirely on who did the work." },
              { strong: "Cat N (non-structural):", text: "damage didn't touch the structure, such as damaged trim, electronics or bodywork that doesn't affect the frame. It's often less serious, but \"non-structural\" can still include a badly damaged interior or safety systems, so it's not automatically minor." },
            ] },
          ],
        },
        {
          heading: "Checking the category before you buy",
          blocks: [
            { type: "p", text: "A car's write-off history is recorded against its registration, not something a seller can hide by not mentioning it. Before agreeing a price, run a dedicated check such as [MOTCO's write-off check](https://www.motco.co.uk/write-off-check) to confirm whether a category is recorded and which one it is." },
            { type: "callout", tone: "tip", title: "Ask for repair evidence on a Cat S car", text: "For anything you're seriously considering, ask who carried out the structural repair and to what standard, and factor that into your offer." },
          ],
        },
      ],
      faq: [
        { q: "Does insurance cost more for a Cat N or Cat S car?", a: "Often yes, and it can be harder to arrange with some insurers. It's worth getting an insurance quote before committing to buy a written-off car, not after." },
        { q: "Can a Cat S car be sold as a normal used car?", a: "Yes, once legally repaired it can be sold and driven, but its write-off history stays on record permanently and usually reduces resale value regardless of repair quality." },
      ],
    },

    "cat-a-and-cat-b-write-offs": {
      slug: "/guides/cat-a-and-cat-b-write-offs",
      title: "Cat A and Cat B write-offs: the ones you can never buy back",
      category: "jargon-buster",
      intro: "Unlike Cat N and Cat S, a Cat A or Cat B write-off can never legally return to the road. Here's the difference.",
      metaTitle: "Cat A and Cat B Write-Offs Explained",
      metaDescription: "What Cat A and Cat B mean, why these vehicles can never be re-registered, and how to check a car isn't one before buying. A guide from ServiceByReg.",
      ...updated,
      sections: [
        {
          heading: "Cat A and Cat B, defined",
          blocks: [
            { type: "p", text: "Cat A and Cat B are the two most severe insurance write-off categories in the UK, both meaning the vehicle must never go back on the road as a whole car. Cat A means the entire vehicle should be crushed; nothing from it, not even parts, is meant to be resold. Cat B means the body shell must be crushed, but usable parts can be salvaged and sold separately." },
          ],
        },
        {
          heading: "Why this matters for buyers",
          blocks: [
            { type: "p", text: "These categories exist for vehicles too dangerous to repair, typically after severe structural or fire damage. A car legitimately registered as Cat A or Cat B should never appear for sale as a complete, driveable vehicle again. If one does, that's a serious red flag, either an identity-cloning scam using a different car's paperwork, or a car illegally rebuilt from a crushed shell." },
            { type: "ul", items: ["Cat A: whole vehicle scrapped, no parts resold", "Cat B: body shell scrapped, individual parts can be resold"] },
          ],
        },
        {
          heading: "How to check before you buy",
          blocks: [
            { type: "p", text: "Because these vehicles shouldn't legally be on the road at all, a write-off check is the clearest way to protect yourself. For the more common Cat N and Cat S categories that DO return to the road legally, [MOTCO's Cat N check](https://www.motco.co.uk/cat-n-check) confirms status against the registration before you commit to a price." },
            { type: "callout", tone: "note", title: "Trust the registration, not the paperwork in hand", text: "Cloned identities exist specifically to get around checks like this by attaching a clean car's details to a written-off one. Always verify the VIN against the registration independently, not just the documents the seller shows you." },
          ],
        },
      ],
      faq: [
        { q: "Can a Cat A or Cat B car ever be re-registered?", a: "No, not as a complete vehicle. These categories are specifically for cars deemed too dangerous to repair and put back on the road under any circumstances." },
        { q: "Are Cat A and Cat B still used as categories today?", a: "The ABI updated its coding system in 2017 to add Cat S and Cat N, but Cat A and Cat B remain in use for the most severe cases, and older records still reference them." },
      ],
    },

    "comparing-vehicle-history-check-providers": {
      slug: "/guides/comparing-vehicle-history-check-providers",
      title: "Are vehicle history check providers all the same?",
      category: "buyer-tips",
      intro: "Most providers pull from similar underlying data, but coverage and presentation vary more than people expect.",
      metaTitle: "Comparing Vehicle History Check Providers",
      metaDescription: "What actually differs between UK vehicle history check providers, and what to look for when choosing one. A guide from ServiceByReg.",
      ...updated,
      sections: [
        {
          heading: "What's actually different between providers",
          blocks: [
            { type: "p", text: "Most UK vehicle history providers license data from the same small pool of sources: DVLA, DVSA, insurers, and finance houses via industry registers. Because of that, the core finance, write-off and stolen markers tend to agree between providers for most cars. What varies more is how much extra detail is included (mileage timelines, service history, valuation), how the report is presented, and how quickly a provider updates its data after a new record is added." },
          ],
        },
        {
          heading: "What to actually compare",
          blocks: [
            { type: "ul", items: [
              { strong: "Data freshness:", text: "some providers update finance and write-off data faster than others after a change is recorded." },
              { strong: "What's included in the base price:", text: "some bundle mileage and service history in; others charge per extra check." },
              { strong: "Clarity of the report:", text: "a plain-language explanation of what a marker means is more useful than a raw data dump." },
              { strong: "Support if something looks wrong:", text: "can you actually reach someone to query a result?" },
            ] },
          ],
        },
        {
          heading: "Making the decision",
          blocks: [
            { type: "p", text: "For most buyers, the practical answer is: pick one reputable provider and run it properly, rather than agonising over which of several near-identical services to use. A [ServiceByReg risk check](/pricing) covers stolen markers, write-off category, outstanding finance and recorded mileage in one search, and the complete report adds the manufacturer's own service record, which most provenance-only providers do not hold at all. If you are weighing up a full-provenance specialist for an import or an unusual history, [MOTCO's own comparison page](https://www.motco.co.uk/motco-vs-carvertical) sets out how its coverage and pricing line up." },
            { type: "callout", tone: "tip", title: "One thorough check beats three shallow ones", text: "Running several free, partial checks and trying to piece them together usually takes longer and leaves more gaps than one paid check covering finance, write-off, mileage and stolen status together." },
          ],
        },
      ],
      faq: [
        { q: "Do all vehicle history checks use the same data?", a: "Largely yes for the core markers (finance, write-off, stolen), since providers license from similar industry sources. Extras like mileage timelines and valuations vary more between providers." },
        { q: "Is a more expensive vehicle history check always better?", a: "Not necessarily. Price often reflects how much extra detail is bundled in, not how accurate the core finance and write-off data is. Check what's actually included before assuming a higher price means better coverage." },
      ],
    },

    "what-a-reg-check-shows": {
      slug: "/guides/what-a-reg-check-shows",
      title: "What a basic reg check shows (and where it falls short)",
      category: "how-to",
      intro: "A free registration lookup is a good first step, but it answers a much narrower question than most buyers assume.",
      metaTitle: "What a Basic Reg Check Shows",
      metaDescription: "What a free UK registration check actually tells you, what it leaves out, and when you need a fuller history check instead. A guide from ServiceByReg.",
      ...updated,
      sections: [
        {
          heading: "What a free reg check typically shows",
          blocks: [
            { type: "p", text: "A basic registration check, run against DVLA's public vehicle data, usually returns the make, colour, engine size, fuel type, tax status, and MOT status of the car. It's free, instant, and a genuinely useful first step before you go any further with a listing." },
          ],
        },
        {
          heading: "What it doesn't cover",
          blocks: [
            { type: "ul", items: ["Outstanding finance owed on the vehicle", "Insurance write-off category", "Stolen vehicle markers", "Full mileage history across MOT tests", "Manufacturer service records"] },
            { type: "p", text: "This is the gap that catches people out: a car can show a perfectly normal basic reg check result and still have serious finance or write-off history, because that data simply isn't part of what a free basic check includes." },
          ],
        },
        {
          heading: "When to go further",
          blocks: [
            { type: "p", text: "Use a basic reg check to confirm the car matches its listing (right colour, engine, tax and MOT status), then run a fuller check before paying any money. [MOTCO's reg check](https://www.motco.co.uk/reg-check) is a good starting point, and can be paired with a full risk check for finance and write-off status once you're seriously interested in a specific car." },
            { type: "callout", tone: "note", title: "Don't stop at the free check", text: "A clean basic reg check tells you the car is real and legally on the road. It tells you nothing about finance owed or write-off history, which are the checks that actually protect your money." },
          ],
        },
      ],
      faq: [
        { q: "Is a free reg check enough before buying a car?", a: "No. It confirms basic details like tax and MOT status, but it doesn't cover finance, write-off or stolen markers, which are the checks most likely to save you from a costly mistake." },
        { q: "What information do I need to run a reg check?", a: "Just the vehicle's registration number. No owner details or consent are required, since it's built on public and licensed data tied to the vehicle itself." },
      ],
    },

    "v5c-logbook-alerts-and-insurance": {
      slug: "/guides/v5c-logbook-alerts-and-insurance",
      title: "V5C logbook alerts: why insurers care too",
      category: "know-the-rules",
      intro: "A reissued V5C isn't just a DVLA administrative detail. It can affect how insurers view a claim.",
      metaTitle: "V5C Logbook Alerts and Insurance",
      metaDescription: "Why a reissued V5C logbook can matter for insurance claims, and how ongoing alerts help protect a car you've bought. A guide from ServiceByReg.",
      ...updated,
      sections: [
        {
          heading: "Why insurers look at V5C history",
          blocks: [
            { type: "p", text: "The V5C, or logbook, records who DVLA has registered as a vehicle's keeper. It's reissued whenever certain details change: a new keeper, a change of address, or a replacement after a lost or damaged copy. Insurers sometimes cross-reference V5C and keeper history when assessing a claim, particularly for theft or total-loss claims, since a pattern of unusual keeper changes can be one factor among several that a claims team looks at more closely." },
          ],
        },
        {
          heading: "A single reissue usually means nothing",
          blocks: [
            { type: "p", text: "Most V5C reissues are entirely routine: someone moved house, or lost their copy. It's a pattern of frequent reissues, especially close together or shortly before a sale, that's worth noticing rather than a single change. None of this proves a problem on its own, but combined with other checks it's one more piece of context." },
          ],
        },
        {
          heading: "Monitoring after you buy",
          blocks: [
            { type: "p", text: "A one-off history check only tells you what's true at the moment you run it. A car can pass clean on the day you buy it and then have something change afterward, a new V5C issued, a keeper change you weren't told about. [MOTCO's V5C logbook alerts](https://www.motco.co.uk/v5c-logbook-alerts) watch a registration for exactly this kind of change over a fixed period, so you're not relying on a single snapshot." },
            { type: "callout", tone: "tip", title: "Keep your own paperwork", text: "If a dispute ever comes up, having your own copy of the sale receipt and V5C section matters more than any alert. Keep them somewhere you can find again." },
          ],
        },
      ],
      faq: [
        { q: "Does a reissued V5C affect my insurance premium?", a: "Not directly on its own. It's the underlying reason for the reissue, and any pattern across several reissues, that an insurer might factor in during a claim, not the reissue itself." },
        { q: "How do I get V5C alerts on a car I've bought?", a: "Vehicle-monitoring services that watch DVLA's registration record can alert you to a V5C reissue or keeper change after your purchase, for a fixed period you choose." },
      ],
    },

    "checking-a-motorbike-or-van-before-you-buy": {
      slug: "/guides/checking-a-motorbike-or-van-before-you-buy",
      title: "Checking a motorbike or van before you buy",
      category: "how-to",
      intro: "Motorbikes and vans get stolen at a disproportionately high rate compared to cars. The checks are similar, but a few things need extra attention.",
      metaTitle: "Checking a Motorbike or Van Before You Buy",
      metaDescription: "Why motorbikes and vans carry extra theft risk, and what to check on the registration before buying either. A guide from ServiceByReg.",
      published: "2026-08-19",
      updated: "2026-08-19",
      updatedLabel: "19 August 2026",
      sections: [
        {
          heading: "Why theft risk is higher for bikes and vans",
          blocks: [
            { type: "p", text: "Motorbikes are easier to physically move without the keys than most cars, and tool theft from vans is common enough that entire vans get taken along with their contents. Both facts push theft and recovery rates for these vehicle types higher than for an average car, which makes a stolen-marker check more important, not less, when you're buying either." },
          ],
        },
        {
          heading: "What to check beyond the basics",
          blocks: [
            { type: "ul", items: [
              { strong: "Frame or chassis number matches the paperwork,", text: "check it physically against the V5C rather than trusting the seller's description of it." },
              { strong: "Import history,", text: "a disproportionate number of used bikes and vans in the UK are grey imports, which affects parts availability and sometimes insurance." },
              { strong: "Modifications,", text: "engine or exhaust changes on a bike, or racking and conversions on a van, can affect insurance validity if undeclared." },
              { strong: "Commercial use history on vans,", text: "high-mileage former courier or trade vans have a harder life than the odometer alone suggests." },
            ] },
          ],
        },
        {
          heading: "Running the check",
          blocks: [
            { type: "p", text: "The same registration-based checks that apply to cars, finance, write-off and stolen markers, apply to bikes and vans too, and are worth running before agreeing a price given the higher baseline theft risk. [MOTCO's stolen-vehicle check](https://www.motco.co.uk/stolen-van-check) covers vans specifically, alongside the same finance and write-off markers." },
            { type: "callout", tone: "note", title: "Buying a van for work?", text: "Ask specifically about commercial insurance history and any fleet-tracking data the previous owner can share. It tells you more about real-world use than a private car's history would." },
          ],
        },
      ],
      faq: [
        { q: "Are motorbikes more likely to be stolen than cars?", a: "Yes, motorbikes are stolen at a higher rate relative to how many are on the road, largely because they're easier to physically remove without the keys." },
        { q: "Does a stolen check work the same for vans as for cars?", a: "Yes, the underlying registration-based check covers any vehicle type, including vans and motorbikes, using the same finance, write-off and stolen-marker data." },
      ],
    },

    "mot-photos-2026-rollout": {
      slug: "/guides/mot-photos-2026-rollout",
      title: "MOT photos are being rolled out in 2026: what UK drivers need to know",
      category: "know-the-rules",
      intro: "DVSA is adding photographs to the MOT process from August 2026. Here is what changes, what stays the same, and why it matters for anyone buying a used car.",
      metaTitle: "MOT Photos 2026: What UK Drivers Need to Know",
      metaDescription: "DVSA is rolling out photos at the MOT test from August 2026. What MOT photos 2026 means for drivers, how the MOT photo check works, and why it aims to deter ghost MOTs.",
      published: "2026-08-21",
      updated: "2026-08-21",
      updatedLabel: "21 August 2026",
      sections: [
        {
          heading: "What the MOT photo rollout actually is",
          blocks: [
            { type: "p", text: "DVSA, the Driver and Vehicle Standards Agency, announced a national rollout of photographs at the MOT test on 29 July 2026, with the rollout beginning on 3 August 2026. In this first phase, around 2,500 MOT garages across Great Britain are being given the feature. The change is set out in DVSA MOT special notice 04-26." },
            { type: "p", text: "Under the new process, an MOT tester can take a photograph of the vehicle during its test using a smartphone, tablet or other compatible device. The image uploads directly to the MOT Testing Service and is not saved to the tester's own camera roll or gallery. It is an extra record of the test, and it does not replace any part of the MOT inspection itself." },
            { type: "p", text: "DVSA's stated aims are to prove the vehicle was physically present at the test, deter fraudulent or \"ghost\" MOTs, improve vehicle identity verification, and increase confidence in MOT testing. The national rollout follows two earlier trials involving a total of 311 garages." },
            { type: "p", text: "This first phase covers Great Britain rather than the whole UK, since Northern Ireland runs its own vehicle testing system separately. More garages are expected to gain the feature over time, but DVSA has not tied that to a single fixed date for every site." },
          ],
        },
        {
          heading: "Why DVSA is bringing in photos at the MOT test",
          blocks: [
            { type: "p", text: "A \"ghost\" MOT is a pass certificate issued for a car that was never properly tested, or in some cases never brought to the garage at all. It undermines the point of the test, because drivers and buyers rely on a valid MOT as evidence a car met a minimum safety standard on a given day. A photograph taken at the test makes that harder to fake, since a genuine test now comes with a dated image of the specific vehicle." },
            { type: "p", text: "The image also helps with vehicle identity verification. Matching the car in front of the tester to the registration on the test record is part of how DVSA keeps the UK MOT history trustworthy, and a dated photo is stronger evidence than a typed entry alone. Over time, that should mean fewer questionable certificates working their way into the second-hand market." },
            { type: "p", text: "For a buyer, the cost of a ghost MOT is not just a wasted certificate. A car that was never properly tested can carry hidden defects the MOT is designed to catch, from worn brakes to structural corrosion, and those only surface later at your expense. Tightening the test record so a pass genuinely reflects a car that was there is a straightforward way to reduce that risk over time." },
            { type: "callout", tone: "note", title: "The photo is not something you can look up yourself", text: "These photographs are not published in the public MOT history that drivers can search. They sit inside the MOT Testing Service for DVSA and garage audit purposes, so the benefit to buyers is a more reliable system overall, not a new image you can pull up on a car you are considering." },
          ],
        },
        {
          heading: "Is uploading a photo compulsory yet?",
          blocks: [
            { type: "p", text: "During this first phase, uploading a photograph is optional. DVSA says it strongly encourages its use and that it may become mandatory in future, but mandatory use has not been confirmed, so a test is still valid without one for now. Any claim that photos are already required is inaccurate." },
            { type: "p", text: "What is already in place is oversight inside the garage. Authorised examiners and managers can see, in their MOT test logs, whether a photograph was uploaded for a test or, if it was not, the reason one was not. That gives DVSA a way to monitor take-up and spot patterns without making the image public." },
            { type: "p", text: "The reason a photo might be missing is usually mundane: a device issue on the day, or a garage still being set up for the feature. A missing photo in this phase is not, on its own, evidence that a test was improper, and DVSA's log approach is designed to tell the difference between a genuine omission and a test that did not happen." },
          ],
        },
        {
          heading: "What this means when you are buying a used car",
          blocks: [
            { type: "p", text: "For a buyer, the practical effect of MOT changes 2026 is gradual rather than instant. As more garages adopt the photo, the MOT record behind a car you are viewing becomes harder to falsify, which is good news for trust in used-car sales. It does not, however, give you a new piece of evidence to look at yourself." },
            { type: "p", text: "The free UK MOT history you can already search by registration still shows test dates, results, advisory notes and the mileage recorded at each test, and that remains the record to read before a viewing. Read the advisory notes as well as the pass or fail line: the same advisory recurring across several years without being fixed is worth asking the seller about, and the mileage at each test should climb steadily. A drop, or a jump that does not match the car's age, is a flag a photo at the MOT test does not change." },
            { type: "p", text: "A fuller check adds the things MOT data never covers, such as outstanding finance, insurance write-off status and stolen markers. [MOTCO's MOT history report](https://www.motco.co.uk/mot-history-check) pulls the official DVSA record together with those wider checks in one search, and a [ServiceByReg report](/vehicle-check) does the same alongside the other risk markers." },
            { type: "p", text: "If you are buying privately or from an unfamiliar seller, run your own check against the registration before agreeing a price rather than trusting a certificate the seller shows you. A fresh result is worth more than a screenshot, because anyone's check is only accurate at the moment it is run." },
          ],
        },
      ],
      faq: [
        { q: "Can I see the photo taken at my car's MOT?", a: "No. The photograph uploads to the MOT Testing Service for DVSA and garage audit use, and it is not published in the public MOT history. Drivers and buyers cannot view it through the normal MOT lookup." },
        { q: "Does the photo replace any part of the MOT inspection?", a: "No. The MOT test itself is unchanged. The photograph is an extra record of the vehicle's presence and identity at the time of the test, not a substitute for any of the standard checks." },
      ],
      note: "The rollout details here are drawn from DVSA MOT special notice 04-26. Dates and figures reflect the official position at the time of writing; check DVSA's current guidance for any later changes before relying on a specific date.",
    },

    "how-to-spot-a-cloned-car": {
      slug: "/guides/how-to-spot-a-cloned-car",
      title: "How to spot a cloned car before you buy",
      category: "how-to",
      intro: "A cloned car wears another vehicle's identity. Here's what that looks like in practice, and how to catch it before money changes hands.",
      metaTitle: "How to Spot a Cloned Car Before You Buy",
      metaDescription: "What car cloning is, the warning signs that show up in paperwork and on the vehicle, and how to check a registration properly. A guide from ServiceByReg.",
      ...fresh,
      sections: [
        {
          heading: "What cloning actually is",
          blocks: [
            { type: "p", text: "Cloning means a stolen or otherwise dubious car is given the identity of a legitimate one, usually by copying number plates and sometimes other details from a similar real vehicle. To a casual buyer, the paperwork can look fine because the registration belongs to a real car somewhere else." },
            { type: "p", text: "That is why a clean-looking V5C in the seller's hand is not enough on its own. You need the car in front of you to match the official record for that registration, not just match the seller's story." },
          ],
        },
        {
          heading: "Checks that catch most clones",
          blocks: [
            { type: "ul", items: [
              { strong: "VIN vs V5C:", text: "the VIN stamped into the car (often visible at the base of the windscreen and on a door pillar plate) must match the VIN on the V5C and any online record for that reg." },
              { strong: "Make, model, colour, fuel:", text: "if the listing is a blue diesel hatchback and the official record for that plate is a silver petrol estate, stop." },
              { strong: "Engine size and year:", text: "small mismatches get explained away too easily. Treat them as a reason to pause, not as a rounding error." },
              { strong: "Plate condition:", text: "fresh plates on an older car, or plates that look different front to back, are worth a closer look alongside the other checks." },
            ] },
            { type: "callout", tone: "note", title: "Do this before you travel with cash", text: "Run the registration checks at home. If the digital record already disagrees with the advert, you have saved yourself a wasted journey." },
          ],
        },
        {
          heading: "Where a history check still helps",
          blocks: [
            { type: "p", text: "A risk check will not always shout \"clone,\" but it can show stolen markers, odd keeper patterns, or write-off data that does not fit the seller's account. A [ServiceByReg report](/vehicle-check) pulls finance, write-off and stolen markers against the registration so you are not relying on the documents the seller chose to bring." },
            { type: "p", text: "Plate-change history matters here too. A recent switch of registration on an otherwise ordinary private sale is not automatically fraud, but it is a prompt to verify the VIN carefully. [MOTCO's plate change check](https://www.motco.co.uk/plate-change-check) is a useful way to see whether that registration has moved between vehicles before you treat the current plates as settled identity." },
          ],
        },
        {
          heading: "If something feels off",
          blocks: [
            { type: "p", text: "Walk away. Do not confront the seller alone, and do not hand over a deposit to \"hold\" the car while you think about it. If you already suspect the vehicle is stolen or cloned, report it rather than trying to complete the deal more carefully." },
          ],
        },
      ],
      faq: [
        { q: "Can a cloned car still have a valid MOT and tax?", a: "Yes. Those systems look up the registration. If the clone is using a real plate from a similar car, MOT and tax can look normal until someone compares the VIN." },
        { q: "Is a HPI clear result enough to rule out cloning?", a: "No. A clear finance and write-off result helps, but cloning is mainly caught by matching the physical VIN and vehicle details to the official record for that plate." },
      ],
      note: "If the VIN, V5C and online record do not agree, treat the sale as unsafe regardless of how convincing the seller sounds.",
    },

    "buying-a-car-still-on-finance": {
      slug: "/guides/buying-a-car-still-on-finance",
      title: "Buying a car that is still on finance",
      category: "know-the-rules",
      intro: "Outstanding finance is one of the most common expensive surprises in private sales. Here's what it means and how to protect yourself.",
      metaTitle: "Buying a Car Still on Finance",
      metaDescription: "What happens if you buy a used car with outstanding PCP or HP finance, how to check before you pay, and what settlement involves. From ServiceByReg.",
      ...fresh,
      sections: [
        {
          heading: "What \"on finance\" means for a buyer",
          blocks: [
            { type: "p", text: "If a car is on hire purchase (HP) or a similar agreement, the finance company usually still owns it until the agreement is settled. The person advertising it may be the registered keeper and still not be free to sell it outright." },
            { type: "p", text: "Personal contract purchase (PCP) works differently in the monthly detail, but the practical risk for a buyer is similar: money may still be owed against that vehicle, and the lender has an interest in it until that balance is cleared." },
          ],
        },
        {
          heading: "What can go wrong if you ignore it",
          blocks: [
            { type: "p", text: "If you pay the seller and the finance is not settled, you can end up in a dispute between you, the seller and the lender. In the worst cases the car can be at risk of recovery action even though you paid in good faith." },
            { type: "p", text: "\"The seller promised to pay it off tomorrow\" is not protection. Either the finance is settled before or at completion, with proof from the lender, or you should not treat the car as cleanly yours." },
          ],
        },
        {
          heading: "How to check before you agree a price",
          blocks: [
            { type: "p", text: "Ask directly whether any finance remains. Then verify it yourself against the registration. A seller who is open about a small settlement figure can still complete a safe sale. A seller who dodges the question is a different problem." },
            { type: "p", text: "A [ServiceByReg report](/vehicle-check) includes an outstanding finance check alongside write-off and stolen markers, so you are not running that risk check in isolation. For a finance-focused lookup, [MOTCO's PCP and HP finance check](https://www.motco.co.uk/pcp-finance-check) is another way to see whether an agreement is still recorded against the plate before you travel." },
            { type: "callout", tone: "tip", title: "Settlement letter, not a verbal promise", text: "If finance exists, get a written settlement figure from the lender, valid to a clear date, and structure payment so that lender is paid. Do not rely on a text message from the seller saying it is already sorted." },
          ],
        },
        {
          heading: "How a clean sale with finance can still work",
          blocks: [
            { type: "ol", items: [
              "Confirm the outstanding balance with the finance company.",
              "Agree whether the seller settles before collection, or whether part of your payment goes straight to the lender.",
              "Keep written proof that the agreement is closed and that any outstanding interest in the vehicle is released.",
              "Only then complete the rest of the private-sale paperwork and keeper change.",
            ] },
            { type: "p", text: "This is normal dealership practice. Private sellers can do it too. The difference is that a private buyer has to insist on the paperwork rather than assuming the process is already handled." },
          ],
        },
      ],
      faq: [
        { q: "Is it illegal to sell a car with finance still on it?", a: "Sellers should not present a financed car as free of encumbrances. A sale can still complete if the finance is properly settled as part of the deal. The danger is paying as if the car is clear when it is not." },
        { q: "Will a finance marker disappear immediately after settlement?", a: "Not always instantly. Databases can lag. Keep the lender's settlement confirmation even if an online check takes a short time to update." },
      ],
      note: "Outstanding finance is separate from write-off and stolen status. A car can be mechanically tidy and still be a poor buy if the lender has not been paid." },

    "how-to-check-a-vin-number": {
      slug: "/guides/how-to-check-a-vin-number",
      title: "How to check a VIN number on a used car",
      category: "how-to",
      intro: "The VIN should match in several places. If it does not, stop the purchase.",
      metaTitle: "How to Check a VIN Number UK",
      metaDescription: "Where to find a car's VIN, how to match it to the V5C, and the tampering signs that mean walk away. A guide from ServiceByReg.",
      ...fresh,
      sections: [
        {
          heading: "What a VIN is",
          blocks: [
            {
              type: "p",
              text: "A VIN is a 17-character vehicle identifier. Modern VINs do not use the letters I, O or Q. It is the same idea people mean when they say chassis number."
            },
            {
              type: "p",
              text: "You want the same VIN on the car, on the V5C, and in any history check you run. One mismatch is enough reason to pause."
            }
          ]
        },
        {
          heading: "Where to look on the car",
          blocks: [
            {
              type: "ol",
              items: [
                "Through the windscreen on the passenger side, on the dashboard plate.",
                "On a sticker or plate in the driver's door aperture (B-pillar area).",
                "Stamped in the engine bay bulkhead on many models.",
                "Sometimes under a flap in the front carpet."
              ]
            },
            {
              type: "p",
              text: "Compare at least two physical locations to the V5C. Cloned cars often get the easy plate right and the stamped metal wrong."
            }
          ]
        },
        {
          heading: "Tampering signs",
          blocks: [
            {
              type: "ul",
              items: [
                {
                  strong: "Fresh grinding or uneven metal",
                  text: "around a stamped VIN."
                },
                {
                  strong: "A windscreen plate that looks loose,",
                  text: "crooked, or newly riveted."
                },
                {
                  strong: "Different fonts or spacing",
                  text: "between VIN locations."
                },
                {
                  strong: "A VIN that does not match",
                  text: "the logbook or the seller's story about the car's age and model."
                }
              ]
            },
            {
              type: "callout",
              tone: "note",
              title: "Missing VIN",
              text: "If a VIN is missing or defaced, walk away. That is not a paperwork inconvenience. It is a major warning."
            }
          ]
        },
        {
          heading: "After you have the number",
          blocks: [
            {
              type: "p",
              text: "Use the registration and VIN together. A [ServiceByReg report](/vehicle-check) checks finance, write-off and stolen markers against the plate, while you confirm the physical VIN matches the documents in your hand."
            },
            {
              type: "p",
              text: "For locations and red flags in one place, MOTCO's guide on [how to check a VIN number](https://www.motco.co.uk/guides/how-to-check-a-vin-number) is a clear companion to the viewing checklist."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Is the VIN the same as the registration number?",
          a: "No. The registration can change with private plates. The VIN should stay with the car for life."
        },
        {
          q: "Can a dealer restamp a VIN legally?",
          a: "Only in controlled, documented cases such as certain repairs under official process. A casual restamp story from a private seller is not something to accept at face value."
        }
      ],
      note: "If anything about the VIN feels altered, do not pay a deposit while you think about it. Leave and report concerns if you suspect crime.",
    },

    "how-to-negotiate-used-car-price": {
      slug: "/guides/how-to-negotiate-used-car-price",
      title: "How to negotiate a used car price in the UK",
      category: "buyer-tips",
      intro: "Empty haggling gets ignored. A calm offer tied to real faults and market prices gets answers.",
      metaTitle: "How to Negotiate a Used Car Price UK",
      metaDescription: "How to negotiate a used car price with evidence from MOT advisories, repair costs and comparable adverts. Practical advice from ServiceByReg.",
      ...fresh,
      sections: [
        {
          heading: "Set a walk-away number first",
          blocks: [
            {
              type: "p",
              text: "Before you message the seller, decide the most you will pay and write it down. That number is for the moment the car looks nicer in person than it should at the asking price."
            },
            {
              type: "p",
              text: "Work back from the advert: market price for similar cars, minus near-term work you can already see. That is a cleaner argument than asking for money off because everyone likes a discount."
            }
          ]
        },
        {
          heading: "Build the offer from evidence",
          blocks: [
            {
              type: "ul",
              items: [
                {
                  strong: "Comparables:",
                  text: "same model, age band, mileage and trim."
                },
                {
                  strong: "MOT advisories:",
                  text: "tyres, brakes, corrosion, leaks from the last one or two tests."
                },
                {
                  strong: "Repair costs as numbers:",
                  text: "two tyres roughly £200 fitted beats it needs tyres."
                },
                {
                  strong: "History risk:",
                  text: "finance, write-off and stolen markers checked before any deposit."
                }
              ]
            },
            {
              type: "p",
              text: "A [ServiceByReg report](/vehicle-check) covers the risk markers so you are not negotiating on a car you should not buy at all."
            },
            {
              type: "callout",
              tone: "tip",
              title: "Say the offer once, then stop talking",
              text: "Give the number and the reasons. Let silence work. Buyers often talk themselves back up to the asking price."
            }
          ]
        },
        {
          heading: "Dealer vs private seller",
          blocks: [
            {
              type: "p",
              text: "Dealers often have more room in extras such as a fresh MOT, tyres or a service than in the screen price. Private sellers usually respond better to one clear, quick offer than to repeated lowball messages."
            },
            {
              type: "p",
              text: "Do not pay a deposit to hold the car before the history, VIN and paperwork are checked. For a fuller negotiation walkthrough with example wording, see MOTCO's guide on [how to negotiate a used car price](https://www.motco.co.uk/guides/how-to-negotiate-used-car-price)."
            }
          ]
        }
      ],
      faq: [
        {
          q: "How much below asking should I offer?",
          a: "There is no fixed percentage. Start from market value minus documented work, then open a little below your target so you have room to move."
        },
        {
          q: "Do MOT advisories really lower the price?",
          a: "Yes, when you turn them into a specific cost. Vague needs some work is easy to dismiss."
        }
      ],
      note: "A discount means nothing if the car has outstanding finance. Check that before money moves.",
    },

    "led-headlight-bulbs-mot-diy-fitting": {
      slug: "/guides/led-headlight-bulbs-mot-diy-fitting",
      title: "Fitting LED headlight bulbs yourself: what the MOT now accepts",
      category: "know-the-rules",
      intro: "From 14 October 2026, approved LED bulbs can pass the MOT in halogen headlamps. This guide is for drivers who buy and fit the bulbs themselves, and want them to pass first time.",
      metaTitle: "Fitting LED Headlight Bulbs Yourself: MOT Rules From October 2026",
      metaDescription: "Approved LEDr bulbs in halogen headlamps pass the MOT from 14 October 2026 if the approval sticker is fitted. What to buy, what the tester checks and what fails.",
      published: "2026-10-17",
      updated: "2026-10-17",
      updatedLabel: "17 October 2026",
      faqSchema: true,
      sections: [
        {
          heading: "Quick answer",
          blocks: [
            { type: "p", text: "Yes, LED bulbs can now pass the MOT in a halogen headlamp, but only the right kind. From 14 October 2026, MOT testers in England, Scotland and Wales accept LED replacement bulbs, called LEDr, when the bulb is ECE approved for that headlamp and its approval sticker is fixed to the car where the tester can see it." },
            { type: "p", text: "No sticker means a fail. A missing, unreadable, damaged or tampered sticker, or one that does not match the bulb, is recorded as the major defect \"light source and lamp not compatible\". A major defect fails the MOT." },
          ],
        },
        {
          heading: "What changed on 14 October 2026",
          blocks: [
            { type: "p", text: "DVSA published [MOT special notice 05-26](https://www.gov.uk/government/publications/mot-special-notice-05-26-approved-led-replacement-bulbs-in-halogen-headlamps/mot-special-notice-05-26-approved-led-replacement-bulbs-in-halogen-headlamps) on 13 October 2026. It took effect on 14 October 2026, when section 4.1.4 of the MOT inspection manual for cars and passenger vehicles was updated. The manual now says a halogen headlamp fitted with an LED replacement light source can be accepted if the bulb is ECE approved for that lamp and carries a matching approval sticker." },
            { type: "p", text: "The change applies to class 3, 4, 5 and 7 vehicles with their existing halogen headlamp units. Class 4 covers most cars and goods vehicles up to 3,000kg design gross weight. Class 7 covers goods vehicles over 3,000kg up to 3,500kg." },
            { type: "ul", items: [
              { strong: "Not covered:", text: "class 1 and class 2 motorcycles." },
              { strong: "Not covered:", text: "halogen headlamps converted to take HID bulbs. For vehicles first used on or after 1 April 1986, the manual says halogen units must not be converted to HID." },
              { strong: "Not covered:", text: "complete replacement headlamp units built for LED or HID. These are not failed as incompatible, but they must still meet the other lamp rules in section 4." },
            ] },
          ],
        },
        {
          heading: "The approval sticker is your proof",
          blocks: [
            { type: "p", text: "The approval sticker is the only evidence the tester uses. Approved LEDr products come with one, and it must be permanently fixed to the vehicle and easy for the tester to see. Testers do not remove lamp covers or bulbs to look for markings, and they do not check the approval number against any database." },
            { type: "p", text: "The sticker must show:" },
            { type: "ul", items: [
              "The LEDr category, for example H11 LEDr",
              "The rated voltage",
              "The ECE approval number",
              "The manufacturer's name",
              "A security feature, such as a hologram",
            ] },
            { type: "p", text: "Sticker designs can differ in size, shape and colour, as long as that information and the security feature are there." },
            { type: "callout", tone: "tip", title: "Fit the sticker when you fit the bulbs", text: "The rule says the sticker must be fixed to the vehicle. A sticker left in the box or in the glovebox does not count. Fix it at the same time as the bulbs, somewhere the tester will see it, and keep it clean and readable." },
          ],
        },
        {
          heading: "Pass or fail: how the tester decides",
          blocks: [
            { type: "p", text: "These outcomes come from special notice 05-26 and section 4.1.4 of the inspection manual. Every fail in this table is a major defect, which means the vehicle fails its MOT." },
            { type: "table", caption: "LED bulbs in halogen headlamps at the MOT, from 14 October 2026", columns: ["What the tester finds", "Result"], rows: [
              ["ECE approved LEDr bulb with a matching sticker fixed to the vehicle and easy to see", "Accepted"],
              ["LED bulb in a halogen headlamp with no sticker", "Fail: light source and lamp not compatible"],
              ["Sticker that cannot be read or is damaged", "Fail: light source and lamp not compatible"],
              ["Sticker that looks tampered with", "Fail: light source and lamp not compatible"],
              ["Sticker that clearly does not match the bulb fitted", "Fail: light source and lamp not compatible"],
              ["Halogen headlamp converted to an HID bulb (vehicle first used on or after 1 April 1986)", "Fail: not covered by the new rule"],
              ["Left and right headlamps that differ in colour, shape or size", "Fail: matched headlamps differ"],
              ["Light colour other than white, white with a blue tinge, or yellow", "Fail: colour does not meet requirements"],
            ] },
            { type: "p", text: "The usual headlamp checks still apply on top of the sticker check, including aim, beam pattern and condition." },
          ],
        },
        {
          heading: "Which LED bulbs count as approved",
          blocks: [
            { type: "p", text: "An approved bulb is an LEDr product that is ECE approved for your headlamp, with the manufacturer's matching sticker. The notice uses H11 LEDr as its example. Being bright, being sold as \"road legal\" or fitting the socket does not make a bulb approved. The sticker is what the tester looks for." },
            { type: "p", text: "Before you buy, check:" },
            { type: "ol", items: [
              "The packaging says LEDr and gives an ECE approval for the bulb type your headlamp takes.",
              "An approval sticker comes in the box, with the category, voltage, approval number, manufacturer name and a hologram or similar feature.",
              "The category on the sticker matches the bulb you are fitting.",
              "You are fitting both sides, so the matched pair of headlamps stays the same colour.",
            ] },
            { type: "p", text: "Before the test, it is worth looking at how the car has done on lights in the past. ServiceByReg shows the [full MOT history for any registration](/vehicle-check) for free, including earlier headlamp fails and advisories. If you want a wider picture of [how the MOT test works](https://www.motco.co.uk/guides/understanding-mot), MOTCO has a plain guide to that too." },
          ],
        },
        {
          heading: "Sources",
          blocks: [
            { type: "p", text: "Every fact in this guide comes from these official pages:" },
            { type: "ul", items: [
              "[MOT special notice 05-26: approved LED replacement bulbs in halogen headlamps](https://www.gov.uk/government/publications/mot-special-notice-05-26-approved-led-replacement-bulbs-in-halogen-headlamps/mot-special-notice-05-26-approved-led-replacement-bulbs-in-halogen-headlamps) (GOV.UK, DVSA, published 13 October 2026)",
              "[MOT inspection manual: cars and passenger vehicles, section 4, lamps, reflectors and electrical equipment](https://www.gov.uk/guidance/mot-inspection-manual-for-private-passenger-and-light-commercial-vehicles/4-lamps-reflectors-and-electrical-equipment) (GOV.UK, see 4.1.4)",
              "[MOT inspection manual updates](https://www.gov.uk/guidance/mot-inspection-manual-for-private-passenger-and-light-commercial-vehicles/updates) (GOV.UK, 14 October 2026 entry)",
              "[MOT test classes and fees](https://www.gov.uk/getting-an-mot/mot-test-fees) (GOV.UK)",
              "[How the MOT defect categories work](https://mattersoftesting.blog.gov.uk/how-the-new-mot-defect-categories-will-work/) (DVSA Matters of Testing blog)",
            ] },
          ],
        },
      ],
      faq: [
        { q: "Do LED headlight bulbs pass the MOT now?", a: "Yes, if they are ECE approved LEDr bulbs fitted in a compatible halogen headlamp, with the matching approval sticker fixed to the vehicle where the tester can see it. This applies from 14 October 2026 in England, Scotland and Wales." },
        { q: "My LED bulbs did not come with a sticker. Will my car fail?", a: "Yes. With no sticker, the headlamp fails for the major defect \"light source and lamp not compatible\", and any major defect fails the MOT. Swap to an approved LEDr product that includes a sticker, or go back to halogen bulbs." },
        { q: "Will the tester take the bulb out to check it?", a: "No. Testers are not expected to remove lamp covers or bulbs, and they do not check the approval number against records. They check that the sticker is there, readable, untampered and matches the bulb." },
        { q: "Can I keep the sticker in the glovebox?", a: "No. The sticker must be permanently fixed to the vehicle and easy for the tester to see." },
        { q: "Does the new rule cover motorbikes?", a: "No. Class 1 and class 2 motorcycles are not covered by special notice 05-26." },
        { q: "Are HID conversion kits allowed now?", a: "No. The change is only about LEDr bulbs. Halogen headlamps on vehicles first used on or after 1 April 1986 must not be converted to HID." },
        { q: "What if I fit a complete LED headlamp unit instead of bulbs?", a: "That is not covered by the LEDr rule. A complete replacement unit built for LED is not failed as incompatible, but it must still meet the other lamp rules in section 4 of the inspection manual." },
        { q: "Does this apply in Northern Ireland?", a: "Special notice 05-26 covers England, Scotland and Wales only." },
      ],
      note: "MOT rules change. This guide reflects the GOV.UK pages listed above as of 17 October 2026. Always follow the current MOT inspection manual.",
    },

    "how-much-is-my-car-tax": {
      slug: "/guides/how-much-is-my-car-tax",
      title: "How much is my car tax? Check by registration",
      category: "how-to",
      intro: "UK car tax (VED) depends on the vehicle, not a one-size fee. The registration is the fastest way to see what that car actually costs to tax.",
      metaTitle: "How Much Is My Car Tax? Check by Reg",
      metaDescription: "How to check UK car tax by registration, what changes the amount, and how a free tax calculator by reg works. A plain guide from ServiceByReg.",
      published: "2026-08-23",
      updated: "2026-08-23",
      updatedLabel: "23 August 2026",
      sections: [
        {
          heading: "What \"car tax\" actually means",
          blocks: [
            {
              type: "p",
              text: "Car tax is Vehicle Excise Duty (VED), often still called road tax. It is the DVLA charge for keeping a vehicle taxed for the road. It is separate from congestion charges, ULEZ, insurance and MOT."
            },
            {
              type: "p",
              text: "The amount is not the same for every car. DVLA sets it from facts tied to that registration, such as first registration date, CO2 figures, fuel type and, for older cars, engine size."
            }
          ]
        },
        {
          heading: "Why checking by registration is the reliable way",
          blocks: [
            {
              type: "p",
              text: "Guessing from the model year alone often goes wrong. Two cars of the same age can sit in different tax bands if one is petrol, one is diesel, or their CO2 figures differ."
            },
            {
              type: "p",
              text: "Entering the plate pulls the vehicle record DVLA uses for tax. That is why searches like \"how much is my car tax\" and \"road tax calculator by reg\" point at a reg lookup, not a generic price list."
            },
            {
              type: "ul",
              items: [
                { strong: "Cars registered from April 2017:", text: "tax is largely driven by CO2 for the first year, then a standard rate, with extras for some expensive cars." },
                { strong: "Older cars:", text: "bands often follow CO2 or engine size rules that applied when the car was new." },
                { strong: "Electric and low-emission cars:", text: "rules have changed over time, so always check the live figure for that plate rather than relying on an old article." }
              ]
            }
          ]
        },
        {
          heading: "How to check how much your car tax is",
          blocks: [
            {
              type: "ol",
              items: [
                "Have the registration ready (the number plate).",
                "Use a free tax calculator that looks up that plate against DVLA vehicle data.",
                "Read the yearly (and any first-year) figure shown for that vehicle.",
                "If you are buying, compare that cost with similar cars before you settle on a price."
              ]
            },
            {
              type: "p",
              text: "ServiceByReg's free [car tax calculator](/car-tax-calculator/) does this from the registration. If you want another independent lookup that works the same way, [MOTCO's car tax calculator](https://www.motco.co.uk/car-tax-calculator) also estimates VED from the plate."
            },
            {
              type: "callout",
              tone: "tip",
              title: "Tax status and tax amount are different",
              text: "A free check may show whether a car is currently taxed. That is not the same as what it will cost you to tax next year. Use the calculator for the amount."
            }
          ]
        },
        {
          heading: "What a tax check will not tell you",
          blocks: [
            {
              type: "p",
              text: "Road tax does not cover outstanding finance, write-off category or stolen markers. Those are separate risk checks. When you are buying, run a [ServiceByReg report](/vehicle-check) for those, and use the tax calculator only for the running cost question."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Can I check car tax with just the registration?",
          a: "Yes. A tax calculator by reg uses DVLA vehicle data linked to the plate. You do not need the V5C in your hand for a basic estimate."
        },
        {
          q: "Is road tax the same as ULEZ?",
          a: "No. VED is national road tax. ULEZ is a separate London clean-air charge. A car can be cheap to tax and still attract a daily ULEZ fee."
        }
      ],
      note: "Tax rates and band rules change. Treat any figure as current only for the day you look it up.",
    },

    "how-long-does-settled-finance-take-to-clear": {
      slug: "/guides/how-long-does-settled-finance-take-to-clear",
      title: "How long does settled car finance take to clear?",
      category: "know-the-rules",
      intro: "Paying off PCP or HP is not the same as seeing the marker vanish overnight. Here is how long lenders usually take to update records, and how long to wait before a clear report is reliable.",
      metaTitle: "How Long Does Settled Car Finance Take to Clear?",
      metaDescription: "Finance companies usually update records in about 3 to 7 working days after settlement. Allow 7 to 10 days before trusting a clear report. From ServiceByReg.",
      published: "2026-09-12",
      updated: "2026-09-12",
      updatedLabel: "12 September 2026",
      sections: [
        {
          heading: "How long lenders take to update after you settle",
          blocks: [
            { type: "p", text: "Once you have paid the settlement figure, the finance company usually takes about 3 working days to 7 working days to update its own records. The exact timing depends on the lender and the terms and conditions of your agreement." },
            { type: "p", text: "It varies because lenders do not all process settlements the same way. Some run overnight or batch updates rather than live changes. Early settlement (paying before the original end date) can sit in a different queue from a final instalment at the natural end of the agreement. Weekends and bank holidays stretch the calendar even when the lender quotes working days." },
            { type: "ul", items: [
              { strong: "Different lenders, different systems:", text: "high-street banks, captive manufacturer finance and specialist lenders do not share one update schedule." },
              { strong: "Early settlement paperwork:", text: "interest rebates and final figures may need an extra check before the account is marked closed." },
              { strong: "Working days only:", text: "a settlement paid on a Friday often will not move through the same clock as one paid on a Monday morning." },
            ] },
          ],
        },
        {
          heading: "Early settlement still needs time to clear",
          blocks: [
            { type: "p", text: "Settling early does not mean the outstanding-finance marker disappears the same day. You may have paid in full, but the lender still has to close the agreement and push that status into the databases that vehicle checks use." },
            { type: "p", text: "Do not assume a same-day clearance just because the money left your account. Treat the settlement confirmation from the lender as the legal proof in that window, and expect the public-facing markers to catch up afterwards." },
          ],
        },
        {
          heading: "What a ServiceByReg report shows after you settle",
          blocks: [
            { type: "p", text: "A [ServiceByReg report](/vehicle-check) reflects what the finance databases show at the time of the check, after their own updates plus the time needed to retrieve that data. It is a snapshot, not a live feed of your lender's internal screen." },
            { type: "p", text: "In most cases, allow 7 to 10 days after settlement before treating a \"clear\" finance result as reliable confirmation that the old agreement has fallen off. Checking too early can still show outstanding finance even when you have already paid." },
            { type: "callout", tone: "tip", title: "Keep the settlement letter", text: "As soon as you settle, ask the lender for a settlement letter or certificate of settlement (sometimes called proof of settlement or a settlement of account). Keep it. If you sell before databases catch up, that document proves the vehicle is clear." },
          ],
        },
        {
          heading: "Selling while finance still shows on reports",
          blocks: [
            { type: "p", text: "If you list on a marketplace, serious buyers often run their own report. Outstanding finance on that check kills trust and can stop a sale even when you know you have paid." },
            { type: "p", text: "Cars with outstanding finance are usually not legal to sell without the lender's agreement, or without proper settlement completed as part of the deal. Presenting a financed car as free of encumbrances is the risk. For how buyers should handle a car that is still on finance, see [buying a car that is still on finance](/guides/buying-a-car-still-on-finance)." },
            { type: "ol", items: [
              "Settle the agreement and get written proof from the lender.",
              "Wait for databases to update (plan on roughly a week, often up to 7 to 10 days for a clear report).",
              "Re-check the registration with a [ServiceByReg report](/vehicle-check) before you list, or again before completion if a buyer is waiting.",
              "If a buyer checks while the marker is still visible, share the settlement letter so they can see the lender has closed the account.",
            ] },
          ],
        },
      ],
      faq: [
        { q: "How long after paying off car finance does it clear?", a: "Finance companies usually update their records in about 3 to 7 working days, depending on the lender and their terms. For a vehicle history report to show clear reliably, allow around 7 to 10 days after settlement in most cases." },
        { q: "Why does my report still show finance after I settled?", a: "Reports read finance databases at the moment of the check. Those databases can lag behind your lender's internal account closure, especially after early settlement, batch updates, weekends or bank holidays. Keep your settlement letter while you wait." },
        { q: "What proof should I keep when selling?", a: "Ask for a settlement letter or certificate of settlement from the lender (sellers sometimes call it proof of settlement). It shows the agreement is closed if a buyer's check still shows the old marker, or if you complete a sale before databases update." },
        { q: "Can I sell a car with outstanding finance?", a: "Usually not without the lender's agreement or proper settlement at completion. Outstanding finance showing on a buyer's report often stops the sale. Settle first, keep written proof, wait for records to update, then re-check before listing or completing." },
      ],
      note: "Timings are typical, not a guarantee from any one lender. Always follow the settlement confirmation and dates your finance company gives you.",
    },
  },

  servicelad: {
    "v5c-and-logbook-alerts-explained": {
      slug: "/guides/v5c-and-logbook-alerts-explained",
      title: "V5C and logbook alerts, explained",
      category: "know-the-rules",
      intro: "A vehicle history check is a snapshot. Here's why what happens to the registration afterwards is worth watching too.",
      metaTitle: "V5C and Logbook Alerts Explained",
      metaDescription: "Why a new or reissued V5C logbook is worth watching after you buy a used car, and what monitoring can catch that a one-off check can't. A guide from ServiceLad.",
      ...updated,
      sections: [
        {
          heading: "What a V5C actually is",
          blocks: [
            { type: "p", text: "The V5C, or logbook, is DVLA's record of who's registered as the keeper of a vehicle. It's not proof of legal ownership, but it's the document most transactions rely on, and it gets reissued whenever certain details change: a new keeper, a change of address, or a replacement after a lost or damaged copy." },
          ],
        },
        {
          heading: "Why a reissue is worth noticing",
          blocks: [
            { type: "p", text: "A single reissue is usually nothing unusual. But a pattern of frequent reissues, especially close together or shortly before a car is advertised for sale, can be worth asking about. It doesn't prove anything on its own, but combined with other checks it can be one more piece of context." },
            { type: "p", text: "The problem with a one-off vehicle history check is that it only tells you what's true at the moment you run it. A car can pass every check clean on the day you buy it and then have something change afterwards: a new V5C issued, a finance agreement taken out, a keeper change you weren't told about. That's the gap ongoing monitoring is built to cover, alerting you if the registration changes after your check rather than leaving you to assume nothing has moved on. [ServiceLad's V5C alerts](/v5c-alerts) watch a registration for exactly this over a fixed period after your purchase." },
          ],
        },
        {
          heading: "What to check before you buy, separately",
          blocks: [
            { type: "p", text: "Before monitoring is even relevant, it's worth running a check on the car's history up to that point, including its MOT record. Past test results, advisories, and the mileage recorded at each test build a picture that a logbook alone doesn't show. [MOTCO's MOT history checker](https://www.motco.co.uk/mot-history) pulls that test-by-test record so you can see recorded mileage and advisories over the car's life, alongside whatever V5C and ownership details you're separately verifying." },
            { type: "callout", tone: "note", title: "Monitoring can't prevent a change, only flag it", text: "Treat an alert as a prompt to ask questions, of the seller, the buyer, or DVLA directly, not as proof of a problem by itself." },
          ],
        },
      ],
      faq: [
        { q: "How long should I monitor a car's V5C after buying it?", a: "A few months covers most issues that would surface early, though some buyers choose longer periods for higher-value cars or private sales with less paper trail." },
        { q: "Does DVLA notify me automatically if a V5C changes?", a: "No. DVLA doesn't proactively alert previous owners or interested parties to keeper changes; that's specifically what a dedicated monitoring service is built to do." },
      ],
    },

    "checking-if-a-car-is-reported-stolen": {
      slug: "/guides/checking-if-a-car-is-reported-stolen",
      title: "How to check if a car has been reported stolen",
      category: "how-to",
      intro: "A stolen marker against a registration is one of the more serious flags a check can turn up. Here's what it means and where it comes from.",
      metaTitle: "How to Check If a Car Has Been Reported Stolen",
      metaDescription: "What a stolen marker means, where the record comes from, and how to check a car's registration before you buy. A guide from ServiceLad.",
      ...updated,
      sections: [
        {
          heading: "What a stolen marker actually records",
          blocks: [
            { type: "p", text: "When a vehicle theft is reported to police, the registration can be flagged on the databases that insurers and check providers reference. A marker staying on a car's record means it was reported stolen and, as far as those records show, not confirmed recovered or resolved." },
          ],
        },
        {
          heading: "Why this matters more than it might seem",
          blocks: [
            { type: "p", text: "Buying a stolen car, even unknowingly, doesn't give you legal ownership of it. If a vehicle you've bought turns out to carry a stolen marker, it can be seized, and getting your money back from the seller afterwards isn't guaranteed, especially with a private sale and no paper trail. A quick check before paying is far cheaper than untangling that afterwards." },
            { type: "ul", items: ["A seller unwilling to meet at their own address, or reluctant to show ID", "A price noticeably below what similar cars are selling for", "Paperwork that doesn't match the seller's name, or a recently reissued V5C without a clear reason", "Pressure to complete the sale quickly, in cash, with no time to check anything"] },
          ],
        },
        {
          heading: "Running the check",
          blocks: [
            { type: "p", text: "A stolen marker sits alongside finance and write-off data as one of the checks worth running before any money changes hands. [MOTCO's stolen vehicle check](https://www.motco.co.uk/stolen-car-check) looks up the registration against the relevant databases and reports back whether a marker is recorded. A [ServiceLad report](/vehicle-check) covers the same marker, and ServiceLad's ongoing V5C alerts keep watching the registration after you buy, in case anything changes later." },
            { type: "callout", tone: "note", title: "If a check does flag a stolen marker", text: "Stop the transaction and report it. Don't confront the seller alone, and don't hand over any money in the meantime." },
          ],
        },
      ],
      faq: [
        { q: "Can I check if a car is stolen for free?", a: "Some basic databases are searchable for free, but coverage is limited. A paid check that combines stolen status with finance and write-off data gives a more complete picture in one search." },
        { q: "What do I do if I've already bought a stolen car unknowingly?", a: "Report it to the police immediately and keep all your purchase paperwork. Acting quickly and keeping records is the best protection if the sale is later disputed." },
      ],
    },

    "ulez-explained": {
      slug: "/guides/ulez-explained",
      title: "What is ULEZ, and does it apply to your car?",
      category: "know-the-rules",
      intro: "ULEZ affects what you pay to drive in parts of London. Here's how to check before you buy, not after.",
      metaTitle: "ULEZ Explained",
      metaDescription: "What ULEZ is, which vehicles are exempt, and how to check whether a car you're considering meets the emissions standard. A guide from ServiceLad.",
      ...updated,
      sections: [
        {
          heading: "What ULEZ actually is",
          blocks: [
            { type: "p", text: "The Ultra Low Emission Zone (ULEZ) is an area covering all London boroughs where vehicles that don't meet minimum emissions standards must pay a daily charge to drive, currently £12.50 for cars and vans. It operates 24 hours a day, every day of the year except Christmas Day, and is enforced by number-plate recognition cameras, not manual checks, so there's no way to avoid it by luck." },
          ],
        },
        {
          heading: "Which cars are exempt",
          blocks: [
            { type: "ul", items: [
              { strong: "Petrol cars:", text: "generally exempt if they meet Euro 4 standards, roughly registered from 2006 onward, though it depends on the specific engine, not just the year." },
              { strong: "Diesel cars:", text: "generally exempt if they meet Euro 6 standards, roughly registered from September 2015 onward." },
              { strong: "Electric vehicles:", text: "always exempt, since they have no tailpipe emissions." },
            ] },
            { type: "p", text: "\"Roughly\" matters here: registration date is a useful shortcut, but the actual rule is about the engine's emissions standard, which can vary between models even within the same model year." },
          ],
        },
        {
          heading: "Checking before you buy",
          blocks: [
            { type: "p", text: "If you're buying a car you'll drive in or near London regularly, check its ULEZ compliance against the exact registration before you commit, rather than assuming based on the year alone. [MOTCO's ULEZ checker](https://www.motco.co.uk/ulez-checker) confirms compliance status by registration number, so you know the daily charge won't be an ongoing cost you hadn't budgeted for." },
            { type: "callout", tone: "tip", title: "Factor it into your offer, not just your budget", text: "A non-compliant car isn't necessarily a bad buy if the price already reflects the ongoing charge, but it's worth knowing before you negotiate, not after." },
          ],
        },
      ],
      faq: [
        { q: "How much is the ULEZ daily charge?", a: "£12.50 per day for cars and vans that don't meet the emissions standard, charged every day the vehicle is driven within the zone, including weekends, except Christmas Day." },
        { q: "Is my car automatically exempt if it's electric?", a: "Yes, fully electric vehicles are always ULEZ-exempt, since the scheme is based on tailpipe emissions and EVs have none." },
      ],
    },

    "finding-your-nearest-mot-test-centre": {
      slug: "/guides/finding-your-nearest-mot-test-centre",
      title: "How to find your nearest MOT test centre",
      category: "how-to",
      intro: "Not every garage is authorised to carry out MOT tests. Here's how to find one that is, quickly.",
      metaTitle: "How to Find Your Nearest MOT Test Centre",
      metaDescription: "How to find an authorised MOT test centre near you, what to check before booking, and how to avoid booking somewhere unauthorised. A guide from ServiceLad.",
      ...updated,
      sections: [
        {
          heading: "Not every garage can do MOTs",
          blocks: [
            { type: "p", text: "An MOT can only be carried out at a garage authorised by DVSA and displaying the official blue MOT sign with three white triangles. Plenty of general repair garages either don't hold this authorisation or only test certain vehicle classes, so it's worth confirming before you book rather than assuming your usual mechanic can do it." },
          ],
        },
        {
          heading: "How to find an authorised centre",
          blocks: [
            { type: "ol", items: [
              "Search by postcode using an official or reputable MOT-centre finder rather than a general search engine, since results should only show DVSA-authorised testers.",
              "Check what vehicle classes the centre is authorised for; some only test cars, not vans or motorbikes.",
              "Compare a few nearby options on price and availability, since MOT fees vary between garages up to the government maximum.",
            ] },
          ],
        },
        {
          heading: "Booking and preparing",
          blocks: [
            { type: "p", text: "Book ahead, particularly if your MOT is due soon, since slots fill up in the days before expiry. [MOTCO's nearest-garage tool](https://www.motco.co.uk/nearest-garage) lists authorised test centres by location, so you can compare options without guessing which local garages are actually certified." },
            { type: "callout", tone: "tip", title: "You can test up to a month early", text: "Testing up to one calendar month (minus a day) before your current MOT expires keeps the same renewal date for next year, rather than shifting it earlier each time." },
          ],
        },
      ],
      faq: [
        { q: "How do I know if a garage is authorised to do MOTs?", a: "Look for the official blue and white MOT sign with three triangles displayed at the premises, or check via an official DVSA-authorised test centre finder before booking." },
        { q: "Can I get an MOT done anywhere in the UK?", a: "Yes, your MOT doesn't need to be done near your registered address; any DVSA-authorised centre anywhere in the UK can carry it out." },
      ],
    },

    "dvla-vehicle-enquiry-explained": {
      slug: "/guides/dvla-vehicle-enquiry-explained",
      title: "DVLA vehicle enquiry: what you can check for free",
      category: "know-the-rules",
      intro: "DVLA's free vehicle enquiry service is a genuinely useful starting point. Here's exactly what it covers.",
      metaTitle: "DVLA Vehicle Enquiry Explained",
      metaDescription: "What DVLA's free Vehicle Enquiry Service shows about a car, what it leaves out, and how to use it properly. A guide from ServiceLad.",
      ...updated,
      sections: [
        {
          heading: "What the Vehicle Enquiry Service shows",
          blocks: [
            { type: "p", text: "DVLA's Vehicle Enquiry Service (VES) is a free, official tool that returns basic details for any registered UK vehicle by registration number: make, colour, year of manufacture, engine size, fuel type, CO2 emissions, current tax status and MOT status. It draws directly from DVLA's own database, so it's accurate and up to date for the fields it does cover." },
          ],
        },
        {
          heading: "What it deliberately leaves out",
          blocks: [
            { type: "p", text: "VES is intentionally limited. For privacy and data-protection reasons, it doesn't show the keeper's name or address, mileage history, finance status, or write-off/stolen markers. It's a vehicle-facts lookup, not a history check, and DVLA has kept it that way by design rather than by accident." },
            { type: "ul", items: ["No keeper name or address", "No finance, write-off or stolen data", "No mileage or service history", "No accident record"] },
          ],
        },
        {
          heading: "Using it alongside a fuller check",
          blocks: [
            { type: "p", text: "VES is a good, free first step: confirming the car's basic specification matches a listing before you go any further. For the finance, write-off, stolen and mileage checks it deliberately excludes, [MOTCO's vehicle enquiry tool](https://www.motco.co.uk/vehicle-enquiry) pairs the same official DVLA facts with those wider checks in one search." },
            { type: "callout", tone: "tip", title: "Use it to catch mismatched listings early", text: "If a seller's ad says one engine size or fuel type and VES shows another, that's worth questioning before you arrange a viewing, not after you've made the trip." },
          ],
        },
      ],
      faq: [
        { q: "Is DVLA's Vehicle Enquiry Service free to use?", a: "Yes, it's a free official government service, and you only need the registration number to use it." },
        { q: "Can I find out who owns a car using DVLA's vehicle enquiry tool?", a: "No. Keeper details are protected and not shown by this service, for privacy reasons. There are separate, more restricted routes to request that information, mainly for specific legal reasons." },
      ],
    },

    "checking-mot-status-regularly": {
      slug: "/guides/checking-mot-status-regularly",
      title: "How often should you check your car's MOT status?",
      category: "how-to",
      intro: "Letting an MOT lapse by accident is more common than people think. Here's how to stay ahead of it.",
      metaTitle: "How Often to Check Your Car's MOT Status",
      metaDescription: "Why it's worth checking MOT status regularly, what happens if it lapses, and how to build a simple habit that avoids the problem. A guide from ServiceLad.",
      ...updated,
      sections: [
        {
          heading: "Why an MOT lapses more easily than you'd think",
          blocks: [
            { type: "p", text: "Unlike road tax, DVLA doesn't send a reminder letter for every MOT renewal in the same consistent way drivers expect, and it's easy to lose track of an exact date across a busy year. Driving without a valid MOT (with narrow exceptions, like driving to a pre-booked test) is illegal, can invalidate your insurance, and typically results in a fixed penalty if caught." },
          ],
        },
        {
          heading: "A simple checking habit",
          blocks: [
            { type: "ul", items: [
              { strong: "Check when you buy the car,", text: "so you know the exact expiry date from day one rather than relying on the seller's word." },
              { strong: "Set a calendar reminder a month before expiry,", text: "giving yourself time to book without last-minute pressure." },
              { strong: "Re-check after any long gap without driving the car,", text: "since it's easy to lose track of dates during a period the car wasn't used regularly." },
            ] },
          ],
        },
        {
          heading: "Checking status quickly",
          blocks: [
            { type: "p", text: "A registration-based MOT status check takes seconds and needs nothing beyond the plate. [MOTCO's free MOT checker](https://www.motco.co.uk/free-mot-checker) shows current status and expiry date instantly, which is worth bookmarking if you'd rather check in thirty seconds than try to remember a date from months ago." },
            { type: "callout", tone: "note", title: "SORN doesn't remove the need to check later", text: "If a car has been off the road under a Statutory Off Road Notification, its MOT will have lapsed, and it'll need a fresh test before it can be taxed and driven again." },
          ],
        },
      ],
      faq: [
        { q: "What happens if I drive with an expired MOT?", a: "It's illegal in most circumstances, can invalidate your car insurance, and typically results in a fixed penalty notice if you're stopped, separate from any MOT retest cost." },
        { q: "Does DVLA remind me before my MOT expires?", a: "Reminder emails and texts are available if you sign up, but they're not automatic for every vehicle, so it's worth checking the exact date yourself rather than relying solely on a reminder arriving." },
      ],
    },

    "basic-mot-checks-before-the-test": {
      slug: "/guides/basic-mot-checks-before-the-test",
      title: "Basic MOT checks you can run yourself before the test",
      category: "how-to",
      intro: "A handful of simple checks before you book can save a wasted trip and a retest fee.",
      metaTitle: "Basic MOT Checks Before the Test",
      metaDescription: "Simple checks you can run at home before an MOT test, and where to look up a car's official MOT record. A guide from ServiceLad.",
      published: "2026-08-19",
      updated: "2026-08-19",
      updatedLabel: "19 August 2026",
      sections: [
        {
          heading: "What you can check in ten minutes",
          blocks: [
            { type: "ul", items: [
              { strong: "Lights:", text: "headlights, brake lights, indicators and number plate light, all common, easy fail points that take seconds to check with someone watching from outside the car." },
              { strong: "Tyre tread and condition:", text: "legal minimum is 1.6mm across the central three-quarters of the tyre, and any visible cuts or bulges are worth addressing before the test, not after a fail." },
              { strong: "Washer fluid and wipers:", text: "an empty washer bottle or a wiper that smears rather than clears is a common, avoidable fail." },
              { strong: "Windscreen chips,", text: "a chip in the driver's line of sight larger than about 10mm can fail the test on its own." },
            ] },
          ],
        },
        {
          heading: "What you can't check yourself",
          blocks: [
            { type: "p", text: "Emissions, brake performance under test conditions, and suspension geometry all need proper equipment, there's a limit to what a visual check at home can catch. If a car has a history of advisories in the same area at previous tests, that's worth a closer look or a quick chat with a garage before booking, rather than waiting to find out at the test itself." },
          ],
        },
        {
          heading: "Checking the official record before you book",
          blocks: [
            { type: "p", text: "Before booking a retest or a first test, it's worth checking what's already on record, previous advisories, past mileage, and results. [MOTCO's MOT check](https://www.motco.co.uk/mot-check) pulls the official DVSA record by registration, so you know what's already been flagged before the tester does." },
            { type: "callout", tone: "tip", title: "Fix repeat advisories before they become a fail", text: "An advisory that shows up two or three tests running without being addressed has a habit of eventually crossing the line into a fail. Worth acting on early rather than waiting." },
          ],
        },
      ],
      faq: [
        { q: "Can I fix an MOT fail myself and avoid a retest fee?", a: "For many faults, yes, if you fix it and get it retested at the same test centre within 10 working days, most centres only charge a partial retest fee or none at all for certain checks." },
        { q: "Do advisories need to be fixed immediately?", a: "Not immediately, an advisory means the item hasn't failed yet but is worth monitoring. Leaving it unaddressed for multiple tests running increases the chance it becomes an actual fail." },
      ],
    },

    "sorn-explained": {
      slug: "/guides/sorn-explained",
      title: "SORN explained: when a car is off the road",
      category: "know-the-rules",
      intro: "A Statutory Off Road Notification stops road tax, but it also changes what you can legally do with the car. Here's the practical version.",
      metaTitle: "SORN Explained: Cars Off the Road",
      metaDescription: "What SORN means, when you need it, what you can and cannot do with a SORN car, and how it affects buying or selling. A guide from ServiceLad.",
      ...fresh,
      sections: [
        {
          heading: "What SORN is",
          blocks: [
            { type: "p", text: "SORN stands for Statutory Off Road Notification. It is how you tell DVLA a vehicle is not being used or kept on a public road. Once a valid SORN is in place, you do not pay vehicle tax for that period." },
            { type: "p", text: "It is not a casual \"I'm not driving it this month\" note. If the car is kept on the public road, including a street outside your house, it generally needs to be taxed and insured in the normal way. SORN is for vehicles kept off public roads, such as on a driveway or in a garage, subject to the current DVLA rules." },
          ],
        },
        {
          heading: "What you can and cannot do",
          blocks: [
            { type: "ul", items: [
              { strong: "You cannot drive it on public roads,", text: "except in very limited cases such as a pre-booked MOT appointment, and even then the rules are strict. Treat \"I'll just pop to the shops\" as not allowed." },
              { strong: "Insurance still matters.", text: "SORN does not automatically mean zero insurance. Check your policy. Some keep cover for fire and theft while the car is laid up." },
              { strong: "MOT can lapse.", text: "A SORN car can sit without a current MOT, but it will need a valid MOT again before it can be taxed and returned to normal road use." },
            ] },
            { type: "callout", tone: "note", title: "SORN is not a parking ticket workaround", text: "If the car is on a public road, SORN is the wrong tool. Tax and insurance are the usual legal requirements there." },
          ],
        },
        {
          heading: "Buying a car that is on SORN",
          blocks: [
            { type: "p", text: "A SORN car can be a fair buy if the price reflects the work needed to put it back on the road. Budget for tax, a possible MOT, and any repairs the last MOT advisories already pointed at." },
            { type: "p", text: "Also run the usual risk checks. SORN status does not clear outstanding finance or a write-off marker. A [ServiceLad report](/vehicle-check) still matters before you pay, and [MOTCO's SORN and tax status check](https://www.motco.co.uk/sorn-check) is a quick way to confirm how the vehicle is currently recorded before you arrange collection." },
          ],
        },
        {
          heading: "After you buy: keep watching the registration",
          blocks: [
            { type: "p", text: "Once the car is taxed again and the keeper details change, paperwork can move quickly. If you want notice of later V5C activity rather than a single snapshot on purchase day, [ServiceLad's V5C alerts](/v5c-alerts) watch the registration over a set period after you buy." },
          ],
        },
      ],
      faq: [
        { q: "Does SORN cancel outstanding finance?", a: "No. Finance is a separate agreement. A SORN car can still have money owed against it." },
        { q: "Can I SORN a car without an MOT?", a: "Yes. Many SORN vehicles have no current MOT. You will usually need a valid MOT before taxing it again for road use." },
      ],
      note: "Always check the current DVLA guidance for SORN, tax and MOT rules before moving a laid-up car. The details can change.",
    },

    "lost-v5c-what-to-do": {
      slug: "/guides/lost-v5c-what-to-do",
      title: "Lost your V5C? What to do next",
      category: "how-to",
      intro: "A missing logbook is common and fixable. It is also a moment when buyers and sellers need to slow down.",
      metaTitle: "Lost V5C: What To Do Next",
      metaDescription: "How to replace a lost V5C logbook, what a missing logbook means in a used-car sale, and how to avoid rushed paperwork mistakes. From ServiceLad.",
      ...fresh,
      sections: [
        {
          heading: "First: treat the V5C as important, not optional",
          blocks: [
            { type: "p", text: "The V5C is DVLA's keeper record for the vehicle. It is not absolute proof of legal ownership on its own, but almost every normal used-car sale expects it. If it is missing, replace it properly rather than inventing a workaround." },
            { type: "p", text: "For sellers, a lost V5C is a delay, not a reason to take cash against a promise that the new one will arrive later. For buyers, purchasing without the correct keeper paperwork is one of the fastest ways into a dispute." },
          ],
        },
        {
          heading: "How replacement usually works",
          blocks: [
            { type: "p", text: "You can apply to DVLA for a replacement V5C if you are the registered keeper. There is a fee, and it takes time to arrive. Apply online or by post using the current DVLA process, and keep confirmation of the application." },
            { type: "p", text: "If you have just bought the car and the seller never sent the keeper slip, that is a different problem. The sale paperwork and DVLA notifications need sorting before you assume the logbook will simply catch up." },
            { type: "callout", tone: "tip", title: "Do not buy on a photocopy alone", text: "A photo of a V5C is easy to reuse. In a private sale, you want the appropriate original sections and a clear plan for the keeper change, not a JPEG." },
          ],
        },
        {
          heading: "If you are buying and the seller has \"lost\" it",
          blocks: [
            { type: "p", text: "Ask when they applied for the replacement, and whether they can show DVLA application proof. A seller mid-replacement can still complete a careful sale. A seller who only notices the V5C is missing when you ask for it is a weaker signal." },
            { type: "p", text: "Run your risk checks against the registration either way. A missing logbook does not prove fraud, and a present logbook does not prove the car is free of finance. A [ServiceLad report](/vehicle-check) covers the finance, write-off and stolen side while the paperwork catches up." },
            { type: "p", text: "Keeper history can also add context when the paper trail is messy. [MOTCO's keeper history check](https://www.motco.co.uk/keeper-history-check) helps you see how often the registration has changed hands before you decide whether to wait for a replacement V5C or walk away." },
          ],
        },
        {
          heading: "After the new V5C arrives",
          blocks: [
            { type: "p", text: "Check the details carefully: name, address, registration, VIN and engine details. If anything is wrong, fix it with DVLA rather than ignoring it." },
            { type: "p", text: "If you want notice of later logbook activity, set up monitoring once you are the keeper. [ServiceLad's V5C alerts](/v5c-alerts) watch for later changes to that registration, which is useful after a paperwork mess where you already know the document trail has been disrupted once." },
          ],
        },
      ],
      faq: [
        { q: "Can I tax a car without a V5C?", a: "Often yes through the current DVLA digital routes if you have the right reference details, but replacing a lost V5C is still worth doing promptly so future sales and keeper changes are not blocked." },
        { q: "Should I buy a car while a replacement V5C is in the post?", a: "Only if you are comfortable with delay and have proof of the DVLA application plus clean risk checks. Many buyers prefer to wait until the new logbook is in hand." },
      ],
      note: "A replacement V5C takes time. Build that delay into any sale plans rather than rushing a cash handover around missing paperwork.",
    },

    "how-to-change-a-flat-tyre-uk": {
      slug: "/guides/how-to-change-a-flat-tyre-uk",
      title: "How to change a flat tyre in the UK",
      category: "how-to",
      intro: "A flat on a UK road is mostly about safety order: where you stop, what you do first, and when to call for help instead.",
      metaTitle: "How to Change a Flat Tyre UK",
      metaDescription: "Safe steps to change a flat tyre on a UK road, what to check in the boot first, and when a space saver is not enough. From ServiceLad.",
      ...fresh,
      sections: [
        {
          heading: "Before you lift the car",
          blocks: [
            {
              type: "ol",
              items: [
                "Get off the live lane if you can. Prefer a hard shoulder only where it is legal and safer than staying put, or take the next exit / park in a car park.",
                "Hazards on. Passengers out on the safe side, away from traffic.",
                "Confirm you have a usable spare or compressor kit, a jack that fits the car, and the correct wheel-nut key."
              ]
            },
            {
              type: "p",
              text: "If you are on a motorway and cannot reach a safe place, stay behind the barrier if you can and use the emergency process for that road. Changing a wheel inches from live traffic is not bravery."
            }
          ]
        },
        {
          heading: "The change, without shortcuts",
          blocks: [
            {
              type: "ul",
              items: [
                {
                  strong: "Loosen nuts slightly",
                  text: "while the wheel is still on the ground."
                },
                {
                  strong: "Jack only at the marked points",
                  text: "in the handbook. Guessing can damage sills."
                },
                {
                  strong: "Fit the spare,",
                  text: "nuts hand-tight, lower the car, then tighten in a star pattern."
                },
                {
                  strong: "Check the spare's pressure",
                  text: "as soon as you can. Many are under-inflated from years in the boot."
                }
              ]
            },
            {
              type: "callout",
              tone: "note",
              title: "Space savers have limits",
              text: "Speed and distance limits apply. They get you home or to a tyre shop. They are not a new set of long-term tyres."
            }
          ]
        },
        {
          heading: "Aftercare that people skip",
          blocks: [
            {
              type: "p",
              text: "Retorque the wheel nuts after a short drive. Repair or replace the damaged tyre properly. If the car keeps losing pressure, look for kerb damage or corrosion on the rim, not just a nail."
            },
            {
              type: "p",
              text: "If this happens on a car you have just bought, treat repeated tyre failures as a prompt to check the wider history and keeper story, not only the rubber. [ServiceLad's V5C alerts](/v5c-alerts) will not fix a puncture, but they help you watch the registration after purchase when small problems start stacking up."
            },
            {
              type: "p",
              text: "For a clear roadside sequence, MOTCO's guide on [how to change a flat tyre in the UK](https://www.motco.co.uk/guides/how-to-change-a-flat-tyre-uk) is worth saving before you need it."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Can I drive on a flat to the next garage?",
          a: "Avoid it. Even a short run on a flat can destroy the tyre and damage the rim."
        },
        {
          q: "What if I only have a sealant kit?",
          a: "Use it only for suitable punctures in the tread area, then get the tyre inspected. Sidewall damage usually means the tyre is finished."
        }
      ],
      note: "If you are unsure about traffic exposure or the jacking points, call breakdown cover. A wheel is cheaper than an injury.",
    },

    "how-to-get-rid-of-car-smells": {
      slug: "/guides/how-to-get-rid-of-car-smells",
      title: "How to get rid of car smells that keep coming back",
      category: "how-to",
      intro: "Air freshener covers a smell. Finding the source is what actually removes it.",
      metaTitle: "How to Get Rid of Car Smells",
      metaDescription: "How to find and remove common car smells, from damp and smoke to food spills, without masking them. A practical guide from ServiceLad.",
      ...fresh,
      sections: [
        {
          heading: "Find the source first",
          blocks: [
            {
              type: "ul",
              items: [
                {
                  strong: "Damp / musty:",
                  text: "wet carpets, blocked cabin-filter drains, leaky door seals, spare-wheel well water."
                },
                {
                  strong: "Smoke:",
                  text: "fabrics, headlining and the ventilation system hold it."
                },
                {
                  strong: "Food / milk / pet:",
                  text: "under seats, seat rails and boot carpet."
                },
                {
                  strong: "Sweet coolant smell:",
                  text: "possible heater matrix or hose issue. That is a mechanical leak, not a cleaning job alone."
                }
              ]
            },
            {
              type: "p",
              text: "Pull out mats, feel the carpet, and check the boot spare well. If the carpet is wet, drying matters more than perfume."
            }
          ]
        },
        {
          heading: "Cleaning that works",
          blocks: [
            {
              type: "p",
              text: "Vacuum thoroughly, including seat rails. Clean hard surfaces with an interior-safe cleaner. Use an enzyme cleaner on organic spills. Replace a dirty cabin pollen filter, because a soaked filter will keep feeding musty air into the face vents."
            },
            {
              type: "p",
              text: "Run the fan on fresh air after cleaning so moisture can leave the ducts. Recirc mode alone will not dry a wet system."
            },
            {
              type: "callout",
              tone: "tip",
              title: "Skip the heavy perfume shortcut",
              text: "Strong sprays on a damp cabin usually make people notice the car more, not less. Dry and clean first."
            }
          ]
        },
        {
          heading: "When the smell means a bigger problem",
          blocks: [
            {
              type: "p",
              text: "Fuel smells, burning smells, or sweet coolant smells need a workshop diagnosis. So does black mould that returns after every wet week: that often means a leak path into the cabin."
            },
            {
              type: "p",
              text: "If you bought the car recently and smells point to long-term neglect, keep records and recheck the car's story. Ongoing [V5C alerts from ServiceLad](/v5c-alerts) will not clear an odour, but they help you stay on top of registration changes while you sort the car itself."
            },
            {
              type: "p",
              text: "For common smell sources and cleanup order, MOTCO's guide on [how to get rid of car smells](https://www.motco.co.uk/guides/how-to-get-rid-of-car-smells) is a solid checklist."
            }
          ]
        }
      ],
      faq: [
        {
          q: "Will ozone treatment fix smoke smell?",
          a: "It can help after a deep clean, but it will not permanently fix a cabin that is still dirty or damp."
        },
        {
          q: "Why does the smell return when I use the heater?",
          a: "Heat can revive residue in the matrix, ducts or cabin filter. Replace the filter and dry the system before buying more scent products."
        }
      ],
      note: "Do not ignore fuel or burning smells. Those are safety issues, not valet issues.",
    },
  },
};
